import type { OverlaySolution } from '@seiza/astro-overlay';

export interface Project {
  id: number;
  profile_id: string;
  profile_name: string;
  name: string;
  display_name: string;
  description: string | null;
  has_files: boolean;
  state: number;
  latest_image_date: number | null;
  /** Target Scheduler's project GUID; the same value in two databases is one project shot by two rigs. */
  guid?: string | null;
}

export interface Target {
  id: number;
  name: string;
  ra: number;
  dec: number;
  active: boolean;
  image_count: number;
  accepted_count: number;
  rejected_count: number;
  has_files: boolean;
}

export interface TargetNavigation {
  id: number;
  project_id: number;
  name: string;
  active: boolean;
  has_files: boolean;
}

export type OrganizationOperation =
  | { kind: 'merge_targets'; source_target_id: number; destination_target_id: number }
  | {
      kind: 'move_images';
      image_ids: number[];
      destination:
        | { target_id: number }
        | { project_id: number; new_target_name: string }
        | { new_project_name: string; new_target_name: string };
    }
  | {
      kind: 'move_target';
      target_id: number;
      destination: { project_id: number } | { new_project_name: string };
    };

export interface OrganizationPreview {
  fingerprint: string;
  source_project_id: number;
  source_project_name: string;
  source_target_id: number;
  source_target_name: string;
  destination_project_id?: number | null;
  destination_project_name: string;
  destination_target_id?: number | null;
  destination_target_name: string;
  images_moved: number;
  exposure_plans_moved: number;
  exposure_plans_created: number;
  warnings: string[];
  /** Set when a moved target changes Target Scheduler profile. */
  profile_change?: { from_profile_id: string; to_profile_id: string } | null;
  exposure_templates_reused?: number;
  exposure_templates_copied?: number;
  /** Each exposure template of a target that changes profile, and the template its plans use there. */
  exposure_template_changes?: {
    source_template_name: string;
    destination_template_name: string;
    reused: boolean;
  }[];
  /** Set when a Planning plan manages the moved target; the move ends that. */
  planning_panel_id?: string | null;
}

export interface OrganizationResult {
  source_project_id?: number;
  project_id: number;
  target_id: number;
  images_moved: number;
}

export interface OrganizationDestinations {
  /** `state` is the Target Scheduler state: 0 Draft, 1 Active, 2 Inactive, 3 Closed. */
  projects: { id: number; name: string; profile_id: string; state?: number | null }[];
  targets: { id: number; name: string; project_id: number }[];
}

export interface ExposureGroup {
  key: string;
  label: string;
  min_seconds: number | null;
  max_seconds: number | null;
}

export interface ProjectProcessingSettings {
  split_exposure_groups: boolean;
  /** The folder below the process directory this project's masters were last saved to. */
  process_folder?: string;
}

export interface Image {
  exposure_group?: ExposureGroup | null;
  id: number;
  project_id: number;
  project_name: string;
  project_display_name: string;
  target_id: number;
  target_name: string;
  acquired_date: number | null;
  /** The observing night, `YYYY-MM-DD`, split at its rig's local noon.
   *  Absent from the single-image view and from older servers. */
  night?: string;
  filter_name: string | null;
  grading_status: number;
  reject_reason: string | null;
  metadata: Record<string, any>; // eslint-disable-line @typescript-eslint/no-explicit-any
  filesystem_path: string | null;
  /** Calibrated and registered copies of this light (absent when none). */
  copies?: ImageCopy[];
  /** The frame's size in pixels, read once per camera setup, to shape its
   *  thumbnail. Absent until the server has read it. */
  frame_size?: { width: number; height: number };
}

/** A calibrated or registered copy of a light that other software wrote. */
export interface ImageCopy {
  /** Pass as `copy` to previews, stars and PSF to view this copy. */
  uuid: string;
  kind: 'calibrated' | 'registered';
  /** `calibrated,cosmetic,registered`. */
  steps: string;
  /** "Calibrated + registered". */
  label: string;
  file_name: string;
  /** The light's own file is this copy: no raw frame yet. */
  primary: boolean;
  /** Found on disk; only the image detail checks. */
  available?: boolean;
}

export interface StarInfo {
  x: number;
  y: number;
  hfr: number;
  fwhm: number;
  brightness: number;
  eccentricity: number;
  /** PSF orientation in radians (elongation direction), when fitted. */
  theta?: number | null;
}

export interface StarDetectionResponse {
  detected_stars: number;
  average_hfr: number;
  average_fwhm: number;
  /** Frame dimensions in pixels; absent from pre-v3 cached results. */
  width?: number | null;
  height?: number | null;
  stars: StarInfo[];
  /** Per-region statistics over the 3×3 grid, computed server-side by
   * seiza-stars. Absent only from a server predating the field. */
  cells?: TiltCell[];
  tilt?: TiltSummaryInfo | null;
}

/** One 3×3 grid cell's aggregate star statistics (server-computed). */
export interface TiltCell {
  row: number;
  col: number;
  star_count: number;
  median_hfr: number | null;
  median_eccentricity: number | null;
  /** Mean elongation direction in radians over [0, π); axial circular mean. */
  mean_theta: number | null;
  /** Direction agreement, 0 (random) to 1 (aligned). */
  theta_coherence: number;
}

export type TiltCornerName = 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';

export interface TiltCorner {
  corner: TiltCornerName;
  hfr: number | null;
}

/** ASTAP-style corner-vs-center tilt and curvature verdict (server-computed). */
export interface TiltSummaryInfo {
  center_hfr: number | null;
  corners: TiltCorner[];
  mean_hfr: number | null;
  tilt_percent: number | null;
  curvature_percent: number | null;
  worst_corner: TiltCornerName | null;
  best_corner: TiltCornerName | null;
}

export type AstrometryAnalysisStatus = 'unavailable' | 'catalog_only' | 'solved' | 'failed';
export type AstrometrySolveMode = 'embedded_wcs' | 'hinted' | 'blind';
export type AstrometryCatalogScope =
  | 'embedded_footprint'
  | 'solved_footprint'
  | 'estimated_field'
  | 'nearby_target';

export interface AstrometryCoordinateSource {
  ra_deg: number;
  dec_deg: number;
  source: string;
  header_keywords?: string[];
}

export interface CatalogHit {
  stable_id: string;
  source: string;
  aliases: string[];
  parent_ids: string[];
  alternate_ids: string[];
  alternate_sources: string[];
  name: string;
  common_name: string;
  kind: string;
  mag: number | null;
  major_arcmin: number | null;
  minor_arcmin: number | null;
  position_angle_deg: number | null;
  ra_deg: number;
  dec_deg: number;
  center_inside: boolean;
  extent_only: boolean;
  distance_from_center_deg: number;
  predicted_prominence: number;
}

export interface PointingResult {
  expected_ra_deg: number;
  expected_dec_deg: number;
  east_offset_arcsec?: number;
  north_offset_arcsec?: number;
  separation_arcsec: number;
  target_in_frame: boolean;
  target_edge_margin_px?: number;
}

export interface AstrometryAnalysis {
  image_id: number;
  status: AstrometryAnalysisStatus;
  mode?: AstrometrySolveMode;
  hint_source?: AstrometryCoordinateSource;
  expected_source?: AstrometryCoordinateSource;
  solution?: OverlaySolution;
  catalog_hits: CatalogHit[];
  catalog_scope?: AstrometryCatalogScope;
  catalog_radius_deg?: number;
  pointing?: PointingResult;
  solver_provenance?: {
    seiza_version: string;
    detection_backend: string;
    star_catalog: { name: string; path: string; format: string; size_bytes: number };
    blind_index?: { name: string; path: string; format: string; size_bytes: number };
  };
  solve_attempt?: {
    outcome: 'solved' | 'no_match' | 'insufficient_stars' | 'decode_error' | 'unsupported_image' | 'resource_unavailable' | 'cancelled' | 'internal_error';
    modes_attempted: AstrometrySolveMode[];
    detected_stars?: number;
    duration_ms: number;
    image_quality_evidence: boolean;
    cacheable: boolean;
  };
  source_fingerprint: {
    canonical_path: string;
    size_bytes: number;
    modified_unix_seconds: number;
    modified_subsec_nanos?: number;
  };
  computed_at: number;
  error?: string;
}

export type BrightTrailRiskLevel = 'low' | 'possible' | 'high';

export interface PixelTrailSegment {
  start: { x: number; y: number };
  end: { x: number; y: number };
}

export interface PixelTrailAlignment {
  status: 'detected' | 'not_detected' | 'not_evaluated';
  not_evaluated_reason?: 'empty_path' | 'too_short' | 'insufficient_coverage';
  aligned_segments?: PixelTrailSegment[];
  start_normal_offset_px: number;
  end_normal_offset_px: number;
  mean_normal_offset_px: number;
  angle_delta_deg: number;
  contrast_adu: number;
  contrast_sigma: number;
  continuity: number;
  coverage: number;
  search_radius_px: number;
}

export interface SatelliteTrackPrediction {
  name: string;
  label: string;
  norad_id?: number;
  cospar_id?: string;
  association: 'predicted';
  element_epoch_utc: string;
  element_age_seconds: number;
  sample_interval_seconds: number;
  clipped_segments: [[number, number], [number, number]][];
  clipped_length_px: number;
  maximum_elevation_deg: number;
  minimum_range_km: number;
  maximum_sunlight_fraction: number;
  maximum_apparent_rate_arcsec_per_second?: number;
  maximum_pixel_rate_px_per_second?: number;
  bright_trail_risk: number;
  risk_level: BrightTrailRiskLevel;
  pixel_alignment?: PixelTrailAlignment;
}

export interface SatelliteAnalysis {
  image_id: number;
  association: 'predicted_not_pixel_detected' | 'predicted_pixel_checked' | 'predicted_with_pixel_alignment';
  seiza_version: string;
  seiza_satellites_version: string;
  pixel_alignment_version: number;
  image_width: number;
  image_height: number;
  exposure: {
    start_utc: string;
    end_utc: string;
    duration_seconds: number;
    latitude_deg: number;
    longitude_deg: number;
    altitude_m: number;
    provenance: string;
    header_keywords: string[];
  };
  catalog: {
    source: string;
    provider?: 'celes_trak_active' | 'seiza_mirror' | 'iau_sat_checker';
    state: 'configured' | 'fresh' | 'downloaded' | 'stale_fallback' | 'cached';
    cache_path?: string;
    size_bytes?: number;
    modified_unix_seconds?: number;
    retrieved_at?: string;
    query_epoch?: string;
    content_sha256?: string;
    warning?: string;
  };
  elements_considered: number;
  propagation_failures: number;
  stale_elements: number;
  tracks: SatelliteTrackPrediction[];
  risk: {
    track_count: number;
    potentially_bright_count: number;
    high_risk_count: number;
    maximum_bright_trail_risk: number;
    pixel_alignment_attempted: boolean;
    pixel_aligned_count: number;
    pixel_aligned_high_risk_count: number;
    reject_recommended: boolean;
  };
  pixel_alignment_error?: string;
  computed_at: number;
}

export interface SatelliteAnalysisStatus {
  analysis?: SatelliteAnalysis;
  orbital_elements_cached: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  status?: 'ready' | 'loading' | 'refreshing';
}

export interface ImageQuery {
  project_id?: number;
  target_id?: number;
  status?: 'pending' | 'accepted' | 'rejected';
  limit?: number;
  offset?: number;
  /** The view's night split over the server's setting (useNightSplit). */
  nights?: 'rig' | 'catalog';
}

export interface UpdateGradeRequest {
  status: 'pending' | 'accepted' | 'rejected';
  reason?: string;
}

/** One grade assignment inside a batch; entries may carry different
 * statuses so an undo can restore a mixed selection in one request. */
export interface BatchGradeEntry {
  image_id: number;
  status: 'pending' | 'accepted' | 'rejected';
  reason?: string | null;
}

export interface BatchGradeResponse {
  updated: number;
  /** The grades the batch replaced, for undo state. */
  previous: BatchGradeEntry[];
}

export interface PreviewOptions {
  size?: 'screen' | 'large' | 'original';
  stretch?: boolean;
  midtone?: number;
  shadow?: number;
  max_stars?: number;
  /** Render a one-shot-color mosaic in colour. Mono frames ignore it. */
  color?: boolean;
  /** A calibrated or registered copy's uuid, to render it instead. */
  copy?: string;
}

// Readiness of an on-demand preview/annotated artifact (the server generates
// it asynchronously on a bounded interactive queue; the frontend batch-polls).
export type GenerationState = 'ready' | 'generating' | 'error';

export interface GenerationStatus {
  state: GenerationState;
  error?: string;
}

// Identifies one artifact for the batch generation-status poll. Mirrors the
// query params of getPreviewUrl / getAnnotatedUrl.
export interface ImagePreviewDescriptor {
  imageId: number;
  kind: 'preview' | 'annotated';
  size: 'screen' | 'large' | 'original';
  stretch?: boolean;
  midtone?: number;
  shadow?: number;
  maxStars?: number;
  /**
   * Must match the flag the `<img>` requested. The server keys colour and
   * greyscale renditions separately, so polling without it would report on
   * the wrong artifact.
   */
  color?: boolean;
  /** The copy the `<img>` requested, if any; keyed separately like colour. */
  copy?: string;
}

/** Where a master comes from: one stack's record, or the library's. */
export type CalibrationMasterSource =
  | { kind: 'mono'; jobId: string; groupIndex: number; artifactRevision: string }
  | { kind: 'color'; jobId: string; artifactRevision: string }
  | { kind: 'library' };

/** A master recorded by one stack. */
export type StackMasterSource = Exclude<CalibrationMasterSource, { kind: 'library' }>;

export interface CalibrationMasterPreviewDescriptor {
  kind: 'calibration_master';
  source: CalibrationMasterSource;
  masterId: string;
  size: 'screen' | 'original';
  midtone?: number;
  shadow?: number;
}

/** What a raw calibration frame's picture shows: the frame, or a flat
 *  divided by its session's median flat. */
export type CalibrationFrameView = 'frame' | 'session';
export type CalibrationFrameSize = 'thumb' | 'screen' | 'original';
/** How wide a window of values the grey scale spans. */
export type CalibrationFrameStretch = 'wide' | 'normal' | 'tight';

export interface CalibrationFramePreviewDescriptor {
  kind: 'calibration_frame';
  frameUuid: string;
  view: CalibrationFrameView;
  size: CalibrationFrameSize;
  stretch: CalibrationFrameStretch;
}

/** The ratio picture of two flat sessions, later ÷ earlier. */
export interface FlatRatioPreviewDescriptor {
  kind: 'flat_ratio';
  earlier: string;
  later: string;
  stretch: CalibrationFrameStretch;
}

export type PreviewDescriptor =
  | ImagePreviewDescriptor
  | CalibrationMasterPreviewDescriptor
  | CalibrationFramePreviewDescriptor
  | FlatRatioPreviewDescriptor;

export interface StackCalibrationMaster {
  id: string;
  kind: 'bias' | 'dark' | 'dark_flat' | 'flat';
  label: string;
  available: boolean;
  unavailable_reason: string | null;
  usages: Array<{
    channel: string;
    session: number;
    lights: number;
    estimated_pedestal_adu: number | null;
  }>;
  preview_url: string | null;
  original_preview_url: string | null;
  fits_url: string | null;
  source_count: number | null;
  rejection_method: string | null;
  masked_samples: number | null;
  rejected_samples: number | null;
  minimum_clean_samples: number | null;
  maximum_clean_samples: number | null;
  /** The source frames still in the library; absent when none are recorded. */
  sources?: CalibrationFrameSpan;
  /** How a library master is told apart in a list, where it has no usages. */
  caption?: string;
  /** A stack's master: the library's record of it, which the Calibration
   *  page's Masters list opens. Absent once the library forgets it. */
  library_id?: string;
}

/** A master the library recorded, with what the library knows of it. */
export interface LibraryCalibrationMaster extends StackCalibrationMaster {
  rig_uuid: string;
  rig_name: string | null;
  created_at: number;
  exposure_s: number | null;
  filter: string | null;
  camera_temp: number | null;
  /** A bias or dark master from too few frames. */
  thin: boolean;
  /** A master from other software, used as it is. */
  external: boolean;
  defect_pixels_replaced: number | null;
  /** Seiza's verdict on a flat master's set of flats. */
  flat_set: {
    measured_frames?: number;
    frames?: number;
    master?: { grade: CalibrationGrade; findings: unknown[] };
    steady_features?: number;
    changing_features?: number;
    carried_features?: number;
  } | null;
  flat_note: string | null;
  /** Set aside by the flat-set check for another set of flats. */
  set_aside: boolean;
  /** Projects whose latest stacks use it. */
  used_by: { project_id: number; name: string | null }[];
}

export interface LibraryCalibrationMasters {
  masters: LibraryCalibrationMaster[];
  notes: string[];
}

/** What a set of calibration frames spans. */
export interface CalibrationFrameSpan {
  frames: number;
  temp_min_c?: number;
  temp_max_c?: number;
  first_night?: string;
  last_night?: string;
  nights: number;
}

export interface StackCalibrationMasters {
  masters: StackCalibrationMaster[];
  notes: string[];
}

/** `preparing`: asked for and in the line while its frames and calibration
 *  are read; `replaced`: prepared into the builds `replaced_by` names. */
export type StackJobState =
  | 'preparing'
  | 'queued'
  | 'running'
  | 'completed'
  | 'failed'
  | 'cancelled'
  | 'replaced';
export type StackGroupState =
  | 'queued'
  | 'running'
  | 'ready'
  | 'skipped'
  | 'error'
  | 'cancelled';

export interface StackFrameDecision {
  image_id: number;
  disposition: 'excluded' | 'reference' | 'accepted' | 'rejected';
  reason: string | null;
  quality_score: number | null;
  matched_stars: number | null;
  registration_rms_pixels: number | null;
  registration_drift_pixels: number | null;
  /** Always null in replies: the server keeps each frame's mapping for itself. */
  registered_mapping: unknown | null;
  normalization_mean_gain: number | null;
  normalization_mean_offset: number | null;
  source_fingerprint: string | null;
  overlap_fraction: number | null;
  integrated_fraction: number | null;
  /** Per-channel noise measured after normalization; noise-weighted stacks only. */
  noise_sigma?: number[] | null;
  /** Per-channel weight in the stack mean; noise-weighted stacks only. The reference weighs 1. */
  integration_weight?: number[] | null;
}

export interface SimilarityTransform {
  scale: number;
  rotation_radians: number;
  translation_x: number;
  translation_y: number;
}

export interface ReferenceRegion {
  x: number;
  y: number;
  width: number;
  height: number;
}

export type ArtifactSearchState = 'queued' | 'running' | 'completed' | 'failed';

export interface ArtifactSearchResult {
  image_id: number;
  filter_name: string;
  acquired_unix_seconds: number | null;
  grading_status: number;
  score: number;
  peak_sigma: number;
  bright_fraction: number;
  dark_fraction: number;
  coverage_fraction: number;
  evidence: 'strong' | 'possible' | 'low';
  direction: 'bright' | 'dark' | 'mixed';
  morphology: 'ring' | 'broad_dark' | 'linear' | 'compact' | 'diffuse' | 'unclassified';
  crop_url: string;
}

export interface ArtifactSearchJob {
  schema_version: number;
  search_id: string;
  database_id: string;
  source_job_id: string;
  source_kind: 'mono' | 'color';
  group_index: number | null;
  artifact_revision: string;
  region: ReferenceRegion;
  state: ArtifactSearchState;
  phase: string;
  total_work_units: number;
  completed_work_units: number;
  created_unix_seconds: number;
  notes: string[];
  results: ArtifactSearchResult[];
  error: string | null;
}

export interface StackInputImage {
  image_id: number;
  grading_status: number;
}

/**
 * How the published stack pixels are laid out. `source_frame` keeps the
 * reference frame's own rotation and is the default; `north_up_east_left`
 * means the build was reprojected onto the shared celestial grid. A
 * `source_frame` stack keeps its reference frame's rotation, turned half a
 * turn where the reference sits on the far side of a meridian flip.
 */
/**
 * How an export arranges its files.
 *
 * `standard` groups by target, PSF Guard's own tree. `wbpp` gives each frame
 * type its own root for WeightedBatchPreprocessing, and folds dark flats in
 * with the darks because WBPP has no dark-flat type of its own.
 */
export type ExportLayout = 'standard' | 'wbpp';

/**
 * How an export's frames land. `symlink` points the tree at the originals,
 * which may sit on a network mount; `reference` places nothing and writes a
 * WBPP runner that names them where they are.
 */
export type ExportPlacement = 'copy' | 'hardlink' | 'reflink' | 'symlink' | 'reference';

/** The choices the export dialog collects for one export. */
export interface ExportChoice {
  layout: ExportLayout;
  /** Also export ungraded (Pending) lights; rejects are never exported. */
  include_pending: boolean;
  /** How the frames land; for a zip download, `reference` means the scripts alone. */
  placement?: ExportPlacement;
  /** For a referenced export: the folder the runner names frames below, as the server sees it. */
  local_root?: string;
  /** For a referenced export: that same folder as PixInsight's machine sees it. */
  remote_root?: string;
  /** The WBPP settings the runner passes; meaningful for the WBPP layout. */
  wbpp?: WbppOptions;
}

export interface StackSkyOrientation {
  convention: 'north_up_east_left' | 'source_frame';
  version: number;
  /**
   * What decided the layout. A `north_up_east_left` stack names the solve it
   * was reprojected from. A `source_frame` stack names the anchor that chose
   * which way up it was published: the reference frame's own solved sky
   * rotation, its side of the pier, or — with neither available — which way
   * most of that stack's own exposure faced.
   */
  source:
    | 'cached_pixel_solve'
    | 'embedded_wcs'
    | 'stack_reference_solve'
    | 'sky_anchor'
    | 'pier_side'
    | 'exposure_majority';
  output_width: number;
  output_height: number;
  source_to_output: unknown;
}

/**
 * The order a build integrated its frames in, which decides what its
 * progressive signal-to-noise curve answers. `capture` starts with the best
 * registration reference, then follows the remaining frames chronologically;
 * `quality` puts the best-graded frames first and asks which are worth keeping.
 */
export type StackFrameOrder = 'capture' | 'quality';

/**
 * How a stack weighs its admitted frames. `noise` (default) weighs each by
 * the inverse of its noise variance; `equal` counts every frame the same.
 */
export type StackWeighting = 'noise' | 'equal';

/**
 * How a stack integrates its frames. One server-wide choice, edited on the
 * Stacking settings page; every job and artifact records the one it used.
 */
export interface StackMethod {
  normalization: 'local_background' | 'global' | 'local';
  weighting: StackWeighting;
  registration: 'quadratic' | 'affine' | 'similarity';
  interpolation: 'lanczos3' | 'bilinear';
  /** Integrate a Bayer frame's photosites rather than demosaiced pixels. */
  bayer_drizzle: boolean;
  /** `auto` is Seiza's choice from each frame's stars and sky. */
  reference: 'auto' | 'best_graded';
  /** `draft` publishes the live stack without the final rejection pass. */
  final_pass: 'reintegrate' | 'draft';
  /**
   * With an automatic reference, degrees a frame's plate-solved rotation
   * must sit inside Seiza's 10° limit to count toward an orientation group.
   */
  reference_rotation_margin_degrees: number;
}

export interface StackMethodSettings {
  method: StackMethod;
  /** Seiza's recommended method, the default. */
  recommended: StackMethod;
  /** What PSF Guard did before the method could be chosen. */
  classic: StackMethod;
  max_reference_rotation_margin_degrees: number;
}

/** One depth on a progressive signal-to-noise curve. */
export interface SnrPoint {
  frames: number;
  exposure_seconds: number;
  /** Pixel-to-pixel noise of the integration, in the stack's own units. */
  noise: number;
  background: number;
  signal: number;
  snr: number;
  channel_noise?: number[];
}

/** Where the curve stands, in one word. */
export type SnrVerdict = 'uncertain' | 'improving' | 'diminishing' | 'plateau' | 'degrading';

/** What the fitted trend says more frames would buy. A prediction. */
export interface SnrProjection {
  /** The gain being priced, as a multiplier: 1.05 is five percent. */
  gain: number;
  extra_frames: number;
  extra_seconds: number;
}

/** A span of the curve where the noise rose instead of falling. */
export interface SnrRegression {
  from_frames: number;
  to_frames: number;
  noise_increase: number;
}

export interface SnrAnalysis {
  measured_frames: number;
  measured_seconds: number;
  best_snr: number;
  final_noise: number;
  /** The exponent in `noise ∝ frames^b`, fitted over the deeper points. */
  noise_exponent: number;
  overall_noise_exponent: number;
  fit_r_squared: number;
  /** What perfect averaging gives: −0.5. */
  ideal_exponent: number;
  /** `noise_exponent` against the ideal, so 1 is textbook square-root gain. */
  efficiency: number;
  frames_for_90_percent: number | null;
  seconds_for_90_percent: number | null;
  frames_for_95_percent: number | null;
  seconds_for_95_percent: number | null;
  projections: SnrProjection[];
  regressions: SnrRegression[];
  verdict: SnrVerdict;
  summary: string;
}

export interface ProgressiveSnr {
  order: StackFrameOrder;
  points: SnrPoint[];
  /** Absent until three depths have been measured. */
  analysis: SnrAnalysis | null;
  /** Why the measured curve cannot support a fitted trend, when applicable. */
  analysis_reason?: string | null;
}

/** Where a channel's final rejection pass has read to. */
export interface FinalPassProgress {
  pass: number;
  passes: number;
  frame: number;
  frames: number;
}

/** The calibration master a channel is building, as Seiza reports it. */
export interface MasterBuildProgress {
  kind: 'bias' | 'dark' | 'dark_flat' | 'flat';
  filter?: string | null;
  stage: 'read' | 'reread' | 'integrate' | 'combine';
  /** Steps of the stage finished: frames, or tiles while a flat combines. */
  done: number;
  total: number;
  /** How far through this build, 0 to 1. It only grows. */
  fraction: number;
  /** Which build this is for the channel, from 1; a flat retry counts. */
  build?: number;
}

export interface StackGroupStatus {
  /** Present while, and after, the final rejection pass runs. */
  final_pass?: FinalPassProgress | null;
  /** Present while the channel builds a calibration master. */
  calibration_progress?: MasterBuildProgress | null;
  exposure_group?: ExposureGroup | null;
  index: number;
  target_id: number;
  target_name: string;
  filter_name: string;
  state: StackGroupState;
  phase: string;
  total_candidates: number;
  eligible_frames: number;
  quality_excluded: number;
  missing_files: number;
  /** Frames left out because the library cannot calibrate them properly;
   *  each frame's decision says why. */
  calibration_excluded?: number;
  processed_frames: number;
  /** Frames restored from a saved accumulator; zero for a fresh build. */
  reused_frames?: number;
  /** Why an existing checkpoint could not be extended, when that happened. */
  resume_note?: string | null;
  accepted_frames: number;
  rejected_frames: number;
  output_channels: number;
  /** What color calibration did for a one-shot-color stack's preview. */
  color_calibration?: StackColorCalibrationOutcome | null;
  /** Why the calibrated preview is out of date: a new solve or Gaia catalog. */
  color_calibration_outdated?: string | null;
  /** The Seiza stretch release that rendered the card preview. */
  preview_stretch_version?: string | null;
  /** Set each time the card preview is rendered again from the FITS. */
  preview_revision?: string | null;
  sky_orientation: StackSkyOrientation | null;
  reference_image_id: number | null;
  total_exposure_seconds: number;
  preview_url: string | null;
  fits_url: string | null;
  /** The curve this build measured, and what it reads as. */
  snr?: ProgressiveSnr | null;
  /** Where the curve is published as JSON, beside the stack's own FITS. */
  snr_url?: string | null;
  error: string | null;
  calibration: AppliedCalibration;
  input_images: StackInputImage[];
  frames: StackFrameDecision[];
}

/**
 * How a stack build calibrates its lights. `auto` applies the safe masters
 * and refuses combinations that damage the result; `on` forces every
 * buildable master, including the refused combinations; `off` stacks raw
 * frames.
 */
export type CalibrationMode = 'auto' | 'on' | 'off';

export interface AppliedCalibration {
  /** Absent on artifacts recorded before the mode existed; those ran `auto`. */
  mode?: CalibrationMode;
  state: 'none' | 'matching' | 'incomplete' | 'applied' | 'off';
  bias_frames: number;
  dark_frames: number;
  dark_flat_frames: number;
  flat_frames: number;
  bias_master: string | null;
  dark_master: string | null;
  dark_flat_master: string | null;
  flat_master: string | null;
  warning: string | null;
  fingerprint: string;
  /**
   * The pedestal subtracted before flat division when the library holds no
   * bias or dark master, fitted from the lights. Absent for stacks that
   * calibrated with measured masters.
   */
  estimated_pedestal_adu?: number | null;
  /**
   * How many calibration sessions the group's lights partitioned into. A
   * multi-night stack calibrates each session with its own masters. Absent
   * or zero on artifacts recorded before sessions existed.
   */
  sessions?: number;
}

export interface StackPreviewJob {
  schema_version: number;
  job_id: string;
  /** Built by the automatic refresh rather than asked for. */
  automatic?: boolean;
  database_id: string;
  project_id: number;
  state: StackJobState;
  accepted_only: boolean;
  created_unix_seconds: number;
  artifact_revision: string;
  cache_version: number;
  stacking_version: string;
  /** The order every group integrated its frames in. */
  order?: StackFrameOrder;
  /** Normalized scoring policy used for frame admission. */
  scoring?: StackScoringSettings;
  /** How every group integrated its frames. */
  method?: StackMethod;
  groups: StackGroupStatus[];
  error: string | null;
  /** While preparing: what was asked for, in a few words. */
  preparing?: string;
  /** Once prepared: the builds this request became. */
  replaced_by?: string[];
}

export interface StackScoringSettings {
  penalty_satellite: number;
  penalty_pointing: number;
  penalty_temporal: number;
  /** Absent on an artifact recorded before these scales existed: 1. */
  penalty_hfr_rise?: number;
  penalty_star_loss?: number;
  hfr_reject_above: number | null;
  star_count_reject_below: number | null;
}

/** Where a stack WBPP made came from. */
export interface WbppStackSource {
  master_file: string;
  output_dir: string;
  exposure_seconds: number | null;
  drizzle: boolean;
  autocrop: boolean;
  imported_unix_seconds: number;
}

/** What taking in a WBPP run's masters did. */
export interface WbppStackImport {
  imported: string[];
  skipped: string[];
}

export interface LatestStackPreviewGroup {
  /** Set when WBPP made this stack and PSF Guard only took it in. */
  wbpp?: WbppStackSource | null;
  job_id: string;
  artifact_revision: string;
  accepted_only: boolean;
  created_unix_seconds: number;
  cache_version: number;
  /** The order this artifact integrated its frames in; old artifacts used capture order. */
  order?: StackFrameOrder;
  /** Scoring policy used by this artifact; old artifacts used calibrated defaults. */
  scoring?: StackScoringSettings;
  /** How this artifact integrated its frames. */
  method?: StackMethod;
  group: StackGroupStatus;
}

export interface LatestStackPreviews {
  schema_version: number;
  database_id: string;
  project_id: number;
  updated_unix_seconds: number;
  groups: LatestStackPreviewGroup[];
}

export type StackActivityKind = 'mono' | 'color' | 'processing';

/** What a stretch, deconvolution or RC-Astro run does and where it stands. */
export interface StackProcessingActivity {
  /** "BlurXTerminator", "Deconvolution", "Stretch", or a chain's steps joined. */
  operation: string;
  /** The channel it works on, such as "R". */
  channel: string;
  /** The stack build and channel group it processes. The channel name
   *  alone can match several groups of one build. */
  stack_job_id?: string;
  group_index?: number;
  /** The step under way while it runs. */
  stage?: string | null;
  /** How far it has got, 0..1, when its step can say. */
  fraction?: number | null;
  /** What holds the stack worker while it waits. */
  waiting_for?: string | null;
  started_unix_seconds?: number | null;
  /** Whether Stop can end it while it runs; an RC-Astro tool can be killed. */
  stoppable: boolean;
}

/** One queued or running stack build or processing run, reported across databases. */
export interface StackActivityEntry {
  kind: StackActivityKind;
  job_id: string;
  database_id: string;
  project_id: number;
  /** The target of the work in flight, when it has one target. */
  target_id?: number | null;
  state: StackJobState;
  label: string;
  detail: string;
  processed_units: number;
  total_units: number;
  created_unix_seconds: number;
  /** Started by the automatic refresh, not by a person. */
  automatic?: boolean;
  /** Place in the line of waiting builds, 0 next; null once running. */
  queue_position?: number | null;
  /** The progress in words, such as the final rejection pass's place. */
  progress_label?: string | null;
  /** Only on `processing` entries. */
  processing?: StackProcessingActivity | null;
}

/** An automatic refresh still waiting for its frames or grades to settle. */
export interface ScheduledRefresh {
  database_id: string;
  database_name: string;
  /** null refreshes every followed project of the database. */
  project_id: number | null;
  project_name: string | null;
  reason: 'arrival' | 'sync' | 'grade';
  due_in_seconds: number;
  /**
   * The channels it expects to restack, then the ones it will stack for the
   * first time (marked "(new)"). The server lists no refresh that expects
   * to do nothing.
   */
  channels?: string[];
  /** Due, and waiting for a quality analysis to finish rather than settling. */
  waiting?: boolean;
  /** What it waits for, in words that fit "Waiting for …". */
  waiting_for?: string | null;
}

/** Processing re-applied to a refreshed stack, waiting for its turn. */
export interface WaitingReapply {
  database_id: string;
  project_id: number;
  target_id?: number | null;
  /** The channel, such as `M44 · R`. */
  label: string;
  /** Following a build a person started: their work, not automatic. */
  personal?: boolean;
  /** What it waits for, in words that fit "Waiting for …". */
  waiting_for?: string | null;
}

export interface StackActivity {
  schema_version: number;
  active: StackActivityEntry[];
  /** Automatic refreshes, soonest first: settling, or due and `waiting`. */
  scheduled?: ScheduledRefresh[];
  /** Re-applies of processing waiting for their turn, out of the line. */
  waiting_reapplies?: WaitingReapply[];
}

export type StackStretchColorStrategy = 'linked' | 'unlinked' | 'luminance-preserving';

export type StackStretchModel =
  | { type: 'identity' }
  | { type: 'linear'; black: number; white: number }
  | { type: 'asinh'; black: number; white: number; strength: number }
  | {
      type: 'percentile-asinh';
      black_percentile: number;
      white_percentile: number;
      strength: number;
    }
  | { type: 'mtf'; shadows: number; midtone: number; highlights: number }
  | {
      type: 'ghs';
      stretch_factor: number;
      local_intensity: number;
      symmetry_point: number;
      protect_shadows: number;
      protect_highlights: number;
      black: number;
      white: number;
    }
  | { type: 'auto-mtf'; target_median: number; shadows_clip: number }
  | {
      type: 'median-anchor';
      target_median: number;
      /** Robust sigmas below the median; null puts the black point at the minimum. */
      black_point_sigma: number | null;
      curves_boost: number;
      hdr_amount: number;
      hdr_knee: number;
      normalize: boolean;
    }
  | {
      type: 'layered';
      target_background: number;
      aggressiveness: number;
      dynamic_range_compression: number;
      /** Pixels that split large structure from detail, 8 through 1024. */
      scale_separation: number;
      contrast_recovery: number;
      saturation_amount: number;
      saturation_boost: number;
      lightness_mask: boolean;
    };

export interface StackStretchRequest {
  model: StackStretchModel;
  color_strategy: StackStretchColorStrategy;
}

export interface StackViewProcessingRequest extends StackStretchRequest {
  deconvolution?: StackDeconvolutionConfig | null;
  rc_astro?: RcAstroProcessing | null;
  /** One-shot-color stacks only; the server calibrates when this is absent. */
  color_calibration?: StackColorCalibration | null;
}

/** One RC-Astro schema parameter's type, default, and range. */
export type ExternalParameterKind =
  | { type: 'float'; default: number; min: number; max: number }
  | { type: 'bool'; default: boolean }
  | { type: 'int'; default: number; min: number; max: number };

export interface ExternalToolParameter {
  name: string;
  /** The CLI flag; null marks a GUI-only parameter that cannot be set. */
  flag: string | null;
  label: string;
  description: string;
  kind: ExternalParameterKind;
}

/** One RC-Astro tool's live contract, read from `rc-astro <tool> --json`. */
export interface ExternalToolSchema {
  schema_version: number;
  cli_version: string;
  key: string;
  name: string;
  ml_version: number | null;
  licensed: boolean;
  license_message: string | null;
  parameters: ExternalToolParameter[];
}

export interface RcAstroCapabilities {
  available: boolean;
  executable?: string;
  tools: ExternalToolSchema[];
  error?: string;
}

export type RcAstroParameterValue = number | boolean;

export interface RcAstroStep {
  tool: string;
  parameters: Record<string, RcAstroParameterValue>;
}

export interface RcAstroProcessing {
  steps: RcAstroStep[];
}

export interface RcAstroStepResult {
  tool: string;
  name: string;
  ml_version: number | null;
  device?: string;
  warnings: string[];
}

export interface StackRcAstroResult {
  cli_version: string;
  steps: RcAstroStepResult[];
  has_stars: boolean;
}

/** A 202 poll answer while a detached processing run waits or computes. */
export interface StackStretchPendingProgress {
  pending: true;
  /**
   * The step under way, e.g. "RC-Astro StarXTerminator". While the run waits
   * for the stack worker, what it waits for: "Waiting for the stack build of
   * M44 · Ha to finish".
   */
  stage?: string;
  /** The chain's overall fraction complete in 0..1; absent while waiting. */
  fraction?: number;
  /** Waiting in the line for the stack worker. */
  queued?: boolean;
  /** What holds the worker while the run waits: "the stack build of M44 · Ha". */
  waiting_for?: string;
  /** Place in the line, 0 next. */
  queue_position?: number;
}

export interface StackDeconvolutionConfig {
  psf_fwhm_pixels: number;
  iterations: number;
  amount: number;
  noise_fraction: number;
  max_correction: number;
}

export interface StackDeconvolutionChannelDiagnostics {
  input_flux: number;
  output_flux: number;
  input_peak: number;
  output_peak: number;
}

export interface StackDeconvolutionResult {
  config: StackDeconvolutionConfig;
  channels: StackDeconvolutionChannelDiagnostics[];
}

export interface StackStretchStatistics {
  min: number;
  max: number;
  median: number;
  mad: number;
  count: number;
}

export interface StackStretchPreview {
  schema_version: number;
  stretch_id: string;
  stretch_version: string;
  deconvolution_version: string | null;
  deconvolution_id: string | null;
  config: StackStretchRequest & { max_analysis_samples: number };
  request?: StackViewProcessingRequest;
  resolved_plan: unknown;
  source_transfer: 'linear' | 'display_referred';
  input_range: { black: number; white: number } | null;
  linked_statistics: StackStretchStatistics;
  channel_statistics: Array<StackStretchStatistics | null>;
  luminance_statistics: StackStretchStatistics | null;
  deconvolution: StackDeconvolutionResult | null;
  /** What color calibration did for a one-shot-color stack. */
  color_calibration?: StackColorCalibrationOutcome | null;
  /** Why this processing is out of date: a new solve or Gaia catalog. */
  color_calibration_outdated?: string | null;
  rc_astro?: StackRcAstroResult | null;
  rc_astro_id?: string | null;
  preview_url: string;
  original_preview_url: string;
  fits_url: string | null;
  /** Present when star removal kept a stars image. */
  stars_preview_url?: string | null;
  stars_original_preview_url?: string | null;
  stars_fits_url?: string | null;
  /**
   * Present when this is the stack's previous revision's result, shown while
   * the server runs the same processing on the refreshed stack.
   */
  reprocessing?: StackReprocessing | null;
}

/** How the processing carried from a stack's previous revision stands. */
export interface StackReprocessing {
  state: 'waiting' | 'queued' | 'running' | 'failed';
  /** The revision the shown result was made from. */
  from_revision: string;
  /** The re-apply's run, once queued: its row in the background jobs. */
  run_id?: string | null;
  error?: string | null;
}

export type StackColorRole = 'luminance' | 'red' | 'green' | 'blue' | 'ha' | 'oiii' | 'sii';
export type StackColorKind = 'rgb' | 'lrgb' | 'narrowband';
export type StackNarrowbandPalette =
  | 'sho'
  | 'soh'
  | 'hso'
  | 'hos'
  | 'osh'
  | 'ohs'
  | 'hoo'
  | 'foraxx-sho'
  | 'foraxx-hoo';

export interface StackColorSource {
  label?: string;
  exposure_group?: ExposureGroup | null;
  role: StackColorRole;
  filter_name: string;
  job_id: string;
  group_index: number;
  artifact_revision: string;
  accepted_frames: number;
  /** WBPP made this channel's stack; it records no frame count. */
  wbpp?: boolean;
  /** Integrated exposure of the channel stack, in seconds. */
  total_exposure_seconds?: number | null;
  reference_image_id: number | null;
  sky_orientation: StackSkyOrientation | null;
  registration_transform: SimilarityTransform | null;
}

export interface StackColorSourceReference {
  job_id: string;
  group_index: number;
  artifact_revision: string;
}

export type StackColorInputSources = Partial<Record<StackColorRole, StackColorSourceReference>>;

/** Where a color build's inputs take their deconvolution and RC-Astro steps
 *  from: what each channel card applied, or the build's own settings.
 *  Absent means `own`, as processing saved before the choice existed. */
export type StackColorInputProcessing = 'own' | 'channels';

export interface StackColorProcessing {
  input_processing?: StackColorInputProcessing;
  background_extraction: StackBackgroundExtraction | null;
  input_deconvolutions: Partial<Record<StackColorRole, StackDeconvolutionConfig>>;
  input_rc_astro?: Partial<Record<StackColorRole, RcAstroProcessing>>;
  /** BlurXTerminator and NoiseXTerminator on the aligned linear color
   *  channels together, before calibration and any stretch. */
  color_rc_astro?: RcAstroProcessing | null;
  input_stretches: Partial<Record<StackColorRole, StackStretchRequest[]>>;
  output_stretches: StackStretchRequest[];
  /** RGB and LRGB only; the server calibrates when this is absent. */
  color_calibration?: StackColorCalibration | null;
}

/** Photometric color calibration against Gaia star colors. */
export interface StackColorCalibration {
  enabled: boolean;
  /** Offset each channel so the sky is the same gray in all three. */
  neutralize_background: boolean;
  /** The star color rendered white: the Sun's, or a Gaia BP-RP color. */
  white_reference: 'solar' | { bp_rp: number };
}

export type StackColorCalibrationSkipReason =
  | 'no_plate_solve'
  | 'no_gaia_catalog'
  | 'too_few_stars'
  | 'failed';

export interface StackColorFit {
  intercept: number;
  slope: number;
  scatter: number;
  stars: number;
}

export type StackColorCalibrationOutcome =
  | {
      status: 'calibrated';
      /** R, G, B multipliers on the normalized channels; green's is 1. */
      gains: [number, number, number];
      offsets: [number, number, number];
      background: [number, number, number];
      white_bp_rp: number;
      red_fit: StackColorFit;
      blue_fit: StackColorFit;
      aperture_radius: number;
      stars_offered: number;
      stars_measured: number;
      /** Stars behind the weaker of the two fits. */
      stars_used: number;
      /** The channels had stretches of their own, applied one by one after
       *  calibration, which undoes most of the balance. */
      stretched_separately?: boolean;
    }
  | { status: 'skipped'; reason: StackColorCalibrationSkipReason; detail: string };

/**
 * A named, database-independent capture of one processing editor's
 * parameters. `view` setups hold a StackViewProcessingRequest; `color` setups
 * hold a StackColorProcessing. The server stores the canonical form and
 * validates the shape on save and import.
 */
export type ProcessingSetupKind = 'view' | 'color';

export interface ProcessingSetup {
  name: string;
  kind: ProcessingSetupKind;
  settings: unknown;
  created_unix_seconds: number;
  updated_unix_seconds: number;
}

/** Also the export document: save it verbatim and the import accepts it. */
export interface ProcessingSetupsDocument {
  schema_version: number;
  setups: ProcessingSetup[];
}

export interface ProcessingSetupsImportResult {
  imported: number;
  replaced: number;
  setups: ProcessingSetup[];
}

export interface StackBackgroundConfig {
  model:
    | {
        kind: 'automatic';
        max_degree: number;
        ridge: number;
        rbf_smoothing: number;
        max_control_points: number;
        allow_radial_basis: boolean;
        minimum_improvement: number;
      }
    | { kind: 'polynomial'; degree: number; ridge: number }
    | { kind: 'radial_basis'; smoothing: number; max_control_points: number };
  samples_per_axis: number;
  sample_radius: number | null;
  search_steps: number;
  sample_rejection_sigma: number;
  fit_rejection_sigma: number;
  fit_rejection_iterations: number;
  border_fraction: number;
  /** The server derives these from fresh pixel solves. */
  protected_regions?: never[];
}

export interface StackBackgroundExtraction {
  config: StackBackgroundConfig;
  correction_mode: 'subtract' | 'divide';
  strength: number;
  protect_catalog_emission: boolean;
}

export interface StackBackgroundFit {
  width: number;
  height: number;
  channels: number;
  model:
    | { kind: 'polynomial'; degree: number; coefficients: number[][] }
    | {
        kind: 'radial_basis';
        smoothing: number;
        centers: number[][];
        coefficients: number[][];
      };
  reference: number[];
  samples: Array<{
    x: number;
    y: number;
    values: number[];
    dispersion: number;
    weight: number;
    status: 'accepted' | 'rejected_noise' | 'rejected_residual';
  }>;
  diagnostics: {
    candidate_samples: number;
    accepted_samples: number;
    rejected_noise: number;
    rejected_residual: number;
    rejection_iterations: number;
    sample_radius: number;
    protected_regions: number;
    model_selection?: {
      selected: string;
      candidates: Array<{ model: string; validation_error: number }>;
    };
  };
}

export interface StackBackgroundProtection {
  reference_image_id: number;
  catalog_version: string | null;
  object_names: string[];
  region_count: number;
  region_fingerprint: string;
}

export type StackColorProgressState =
  | 'pending'
  | 'running'
  | 'completed'
  | 'skipped'
  | 'reused'
  | 'failed';

export type StackColorProgressPhase =
  | 'loading_sources'
  | 'background_preparation'
  | 'registering_sources'
  | 'deconvolving_inputs'
  | 'aligning_sources'
  | 'normalizing_inputs'
  | 'rc_astro_color'
  | 'calibrating_color'
  | 'stretching_inputs'
  | 'rc_astro_inputs'
  | 'composing_color'
  | 'stretching_output'
  | 'writing_fits'
  | 'rendering_original'
  | 'rendering_screen'
  | 'publishing_artifacts';

export interface StackColorPhaseProgress {
  phase: StackColorProgressPhase;
  label: string;
  state: StackColorProgressState;
  completed_units: number;
  total_units: number;
}

export interface StackColorProgress {
  completed_units: number;
  total_units: number;
  active_phase: StackColorProgressPhase | null;
  current_role: StackColorRole | null;
  current_stage: number | null;
  stage_count: number | null;
  phases: StackColorPhaseProgress[];
}

export type StackColorCrop = 'none' | 'bounds' | 'inscribed';

export interface StackColorChannelCoverage {
  role: StackColorRole | null;
  name: string;
  covered_pixels: number;
  center_offset_pixels: number;
  off_center: boolean;
}

export interface StackColorCropReport {
  grid_width: number;
  grid_height: number;
  x: number;
  y: number;
  width: number;
  height: number;
  retained_fraction: number;
  channels: StackColorChannelCoverage[];
}

export interface StackColorJob {
  source_family_key?: string;
  schema_version: number;
  job_id: string;
  database_id: string;
  project_id: number;
  target_id: number;
  target_name: string;
  kind: StackColorKind;
  palette: StackNarrowbandPalette | null;
  label: string;
  state: StackJobState;
  phase: string;
  processed_channels: number;
  total_channels: number;
  progress: StackColorProgress;
  created_unix_seconds: number;
  artifact_revision: string;
  cache_version: number;
  stacking_version: string;
  background_version: string;
  deconvolution_version: string;
  linear_input_id: string | null;
  sources: StackColorSource[];
  crop: StackColorCrop;
  crop_report: StackColorCropReport | null;
  processing: StackColorProcessing | null;
  resolved_input_stretches: Partial<Record<StackColorRole, unknown[]>>;
  resolved_input_deconvolutions: Partial<Record<StackColorRole, StackDeconvolutionResult>>;
  resolved_input_rc_astro?: Partial<Record<StackColorRole, StackRcAstroResult & {
    fits_url: string;
    stars_fits_url?: string | null;
  }>>;
  /** The RC-Astro result on the color channels together. */
  resolved_color_rc_astro?: StackRcAstroResult & {
    fits_url: string;
    stars_fits_url?: string | null;
  } | null;
  resolved_output_stretches: unknown[];
  /** For a build that follows the channel cards: what each card had applied
   *  when the build was prepared. A card that changed since makes the build
   *  out of date. */
  followed_channel_processing?: Partial<Record<StackColorRole, {
    deconvolution?: StackDeconvolutionConfig;
    rc_astro?: RcAstroProcessing;
  }>>;
  resolved_backgrounds: Partial<Record<StackColorRole, StackBackgroundFit>>;
  resolved_background_protection: Partial<Record<StackColorRole, StackBackgroundProtection>>;
  background_protection_fallbacks: Partial<Record<StackColorRole, string>>;
  /** What color calibration did; absent when it was off or never ran. */
  color_calibration?: StackColorCalibrationOutcome | null;
  preview_url: string;
  fits_url: string;
  error: string | null;
  outdated: boolean;
  outdated_reason: string | null;
  /** While its request is prepared: what was asked for, in a few words. */
  preparing?: string;
  /** The composition a request being prepared became. */
  replaced_by?: string[];
}

export interface StackColorAvailableRole {
  role: StackColorRole;
  filter_name: string;
}

export interface StackColorTargetAvailability {
  source_candidates?: StackColorSource[];
  target_id: number;
  target_name: string;
  available_roles: StackColorAvailableRole[];
  ambiguous_roles: StackColorRole[];
  unmapped_filters: string[];
  rgb_available: boolean;
  lrgb_available: boolean;
  narrowband_palettes: StackNarrowbandPalette[];
}

export interface StackColorCatalog {
  schema_version: number;
  database_id: string;
  project_id: number;
  targets: StackColorTargetAvailability[];
  jobs: StackColorJob[];
}

export interface SiteBanner {
  title: string;
  message: string;
  link_text?: string;
  link_url?: string;
}

export type AstrometryResourceStatus =
  | 'not_configured'
  | 'missing'
  | 'available'
  | 'invalid';

export interface AstrometryResourceCapability {
  name: string;
  status: AstrometryResourceStatus;
  path?: string;
  format?: string;
  size_bytes?: number;
  modified_unix_seconds?: number;
  error?: string;
}

export interface AstrometryCapabilities {
  seiza_version: string;
  seiza_fits_version: string;
  data_dir?: string;
  resources: {
    objects: AstrometryResourceCapability;
    stars: AstrometryResourceCapability;
    star_identifiers: AstrometryResourceCapability;
    blind_index: AstrometryResourceCapability;
    transients: AstrometryResourceCapability;
    minor_bodies: AstrometryResourceCapability;
    gaia_photometry?: AstrometryResourceCapability;
  };
  features: {
    object_association: boolean;
    object_name_search: boolean;
    stellar_name_search: boolean;
    hinted_solve: boolean;
    blind_solve: boolean;
    transient_annotations: boolean;
    minor_body_annotations: boolean;
    color_calibration?: boolean;
  };
}

export interface AstrometryValidationReport {
  all_configured_valid: boolean;
  resources: Array<{
    name: string;
    status: AstrometryResourceStatus;
    path?: string;
    validated: boolean;
    error?: string;
  }>;
}

export type CatalogInstallPreset =
  | 'solver_lite'
  | 'solver_gaia'
  | 'blind_deep'
  | 'blind_deep_gaia20'
  | 'gaia_photometry';

export interface CatalogInstallProgress {
  running: boolean;
  phase: 'idle' | 'manifest' | 'downloading' | 'installing' | 'complete' | 'error';
  message: string;
  preset?: CatalogInstallPreset;
  output_dir?: string;
  file_name?: string;
  files_completed: number;
  files_total: number;
  bytes_completed?: number;
  bytes_total?: number;
  written_bytes?: number;
  installed_version?: string;
  error?: string;
  started_at?: number;
  finished_at?: number;
}

export interface CatalogInstallStatus {
  started: boolean;
  progress: CatalogInstallProgress;
}

export type ExternalMasterPolicy = 'prefer' | 'fallback' | 'ignore';

/** Which darks a master starts from when several sets match a light. */
export type DarkChoice = 'complete' | 'nearest';

export interface CalibrationSettings {
  /** Configured override in degrees; null when the library default applies. */
  rotation_tolerance_deg: number | null;
  /** What applies with no override, for labeling the placeholder. */
  default_rotation_tolerance_deg: number;
  /** How masters built by other software are used; `prefer` by default. */
  external_masters: ExternalMasterPolicy;
  /** Mask detected stars when building flat masters; off by default. */
  flat_star_masking: boolean;
  /** Days a dark may be from a light; null when the default applies. */
  dark_reach_days?: number | null;
  default_dark_reach_days?: number;
  /** Darks from one night that make a master alone; null for the default. */
  complete_dark_frames?: number | null;
  default_complete_dark_frames?: number;
  /** `complete` by default: the nearest complete set beats a nearer short one. */
  dark_choice?: DarkChoice;
  /** Days; zero lifts the limit. */
  flat_max_age_days?: number | null;
  default_flat_max_age_days?: number;
  /** Stacks leave out lights that cannot be calibrated. */
  exclude_uncalibrated?: boolean;
  /** Every tunable check, with its range and default. */
  fields?: CalibrationRuleField[];
  /** The server's values by key; a key not listed uses the default. */
  rules?: CalibrationRuleValues;
  /** Every rig an open catalog records, and every rig with values of its own. */
  rigs?: CalibrationRigSettings[];
}

/** What a light check does when it finds something. */
export type CalibrationCheckMode = 'flag' | 'advisory' | 'off';

/** A frame grade's two limits, `null` for never. Stored as Seiza measures them. */
export interface CalibrationGradeLimits {
  suspect: number | null;
  poor: number | null;
}

export type CalibrationRuleValue = number | boolean | string | CalibrationGradeLimits;

/** Settings by key. A key not listed inherits. */
export type CalibrationRuleValues = Record<string, CalibrationRuleValue>;

export type CalibrationRuleFieldKind =
  | { type: 'number'; min: number; max: number; step: number; unit: string; zero?: string }
  | { type: 'count'; min: number; max: number; unit: string }
  | { type: 'toggle' }
  | { type: 'choice'; options: { value: string; label: string }[] }
  /** `levels`: the levels the check raises on its own. */
  | { type: 'check'; levels: ('poor' | 'check')[] }
  | {
      type: 'limits';
      min: number;
      max: number;
      step: number;
      unit: string;
      /** Show the limits times a hundred. */
      percent: boolean;
      lower_is_worse: boolean;
    };

/** One tunable calibration check. */
export interface CalibrationRuleField {
  key: string;
  group: 'matching' | 'darks' | 'flat_groups' | 'checks' | 'grades';
  label: string;
  help: string;
  /** What changing it changes: which frames match, what is flagged, or how frames grade. */
  effect: 'matching' | 'flags' | 'grades';
  kind: CalibrationRuleFieldKind;
  default: CalibrationRuleValue;
}

/** One rig and the values it sets for itself. */
export interface CalibrationRigSettings {
  rig_uuid: string;
  name: string;
  /** Frame size and binning. */
  detail?: string;
  /** The databases that record it, by name. */
  databases: string[];
  /** The same databases by id, in the same order. Absent from older servers. */
  database_ids?: string[];
  frames: number;
  /** Its own values; a key not listed inherits. */
  rules: CalibrationRuleValues;
}

/** How much of the processor work may take, as fractions of logical cores. */
export interface WorkerSettings {
  /** Work a person waits on: builds they start, quality scans, previews. */
  interactive_ratio: number;
  /** Work nobody waits on: automatic refreshes, pre-generation, backfill. */
  background_ratio: number;
  default_interactive_ratio: number;
  default_background_ratio: number;
  logical_cores: number;
}

/** Whether, and how soon, remembered stack previews rebuild on their own. */
export interface StackAutomationSettings {
  /** Rebuild when frames arrive, a sync lands, or grades change. */
  automatic_previews: boolean;
  /** Minutes an arrival or sync settles before the refresh runs. */
  arrival_delay_minutes: number;
  /** Minutes a grade change settles before the refresh runs. */
  grade_delay_minutes: number;
  default_arrival_delay_minutes: number;
  default_grade_delay_minutes: number;
  max_delay_minutes: number;
  /** Refreshes also stack channels with no stack yet, once a frame is recent. */
  build_new_channels: boolean;
  /** How recent, in days; 0 takes channels of any age. */
  new_channel_window_days: number;
  default_new_channel_window_days: number;
  /**
   * A stack that refreshes, by an automatic refresh or a rebuild, gets the
   * processing its previous revision showed. On by default; absent from an
   * older server.
   */
  reapply_processing?: boolean;
}

/** How hard WBPP works at local normalization: its own presets. */
export type WbppQuality = 'maximum' | 'good' | 'fast';
/** Fast Integration of the light groups; WBPP switches groups of 150+ frames to it on its own unless told off. */
export type WbppFastIntegration = 'off' | 'auto' | 'on';
export type WbppDrizzle = 'off' | '2x' | '3x';
export type WbppRejection =
  | 'percentile_clip'
  | 'winsorized_sigma'
  | 'linear_fit'
  | 'esd'
  | 'rcr'
  | 'auto';

/** What a WBPP run is asked for beyond the frames. */
export interface WbppOptions {
  quality: WbppQuality;
  fast_integration: WbppFastIntegration;
  drizzle: WbppDrizzle;
  /** Absent leaves WBPP's default (on). */
  autocrop?: boolean;
  /** Absent leaves WBPP's default (automatic). */
  rejection?: WbppRejection;
}

export const DEFAULT_WBPP_OPTIONS: WbppOptions = {
  quality: 'maximum',
  fast_integration: 'off',
  drizzle: 'off',
};

export interface ExportSettings {
  /** The layout the export dialog starts from. */
  default_layout: ExportLayout;
  /** The WBPP settings an export or an in-app run starts from. */
  wbpp: WbppOptions;
}

export interface PixInsightInstall {
  binary: string;
  root: string;
  bpp_main: string;
  wbpp_version: string | null;
}

export interface PixInsightDetection {
  install: PixInsightInstall | null;
  source: 'configured' | 'detected' | null;
  /** The places tried, in order. */
  checked: string[];
  /** Why a configured path was not taken. */
  problem: string | null;
}

/** How PixInsight gets the display it needs on the server. */
export type DisplayPlan = { kind: 'own' } | { kind: 'xvfb'; path: string } | { kind: 'missing' };

export interface PixInsightSettings {
  /** The executable the settings name, if any. */
  binary: string | null;
  detection: PixInsightDetection;
  display: DisplayPlan;
  /** Whether a run could start now. */
  ready: boolean;
  /** The runs folder the settings name; absent means the database's export directory, else the cache. */
  runs_dir: string | null;
  /** Bytes free where that folder is, when known. */
  runs_dir_free_bytes: number | null;
}

export interface WbppOutputFile {
  /** Below the run's output folder, with forward slashes. */
  path: string;
  size_bytes: number;
  kind: 'master' | 'calibrated' | 'registered' | 'log' | 'other';
}

/** What became of a WBPP run's masters as stacks for the Stacks view. */
export interface WbppStacksTakenIn {
  state: 'importing' | 'done' | 'error';
  imported: number;
  skipped: number;
  error: string | null;
}

export interface WbppRunProgress {
  running: boolean;
  /** `planning`, `launching`, `running`, `complete`, `error`, `cancelled`, or empty. */
  stage: string;
  scope: string;
  work_dir: string;
  output_dir: string;
  /** Bytes free where the run folder is, when the run began. */
  free_bytes_at_start: number | null;
  options: WbppOptions | null;
  frames: number;
  lights: number;
  missing_files: number;
  /** Calibration frames the library keeps out: graded poor or kept out by hand. */
  kept_out_frames?: number;
  /** Lights left out because they can't be calibrated, as stacks leave them out. */
  uncalibrated_lights?: number;
  /** Calibration settings that differ from the defaults, with who set them. */
  calibration_settings?: string[];
  /** Measured flat changes the lights' flats were chosen around, with their windows. */
  flat_changes?: string[];
  /** Lights whose flats are from across such a change, which WBPP may not calibrate as a stack would. */
  flat_warnings?: number;
  command: string | null;
  pid: number | null;
  started_at: number | null;
  finished_at: number | null;
  exit_code: number | null;
  wbpp_stage: string | null;
  wbpp_steps: number;
  wbpp_elapsed: string | null;
  log_path: string | null;
  log_tail: string[];
  log_errors: string[];
  outputs: WbppOutputFile[];
  error: string | null;
  project_id: number | null;
  /** The masters' save below the process directory, when asked for. */
  publish: WbppPublishOutcome | null;
  /** The target the run was for, when it was for one. */
  target_id?: number | null;
  /** Taking the masters in as stacks, once a project's run completes. */
  stacks?: WbppStacksTakenIn;
}

export interface WbppPublishOutcome {
  total?: number;
  current_file?: string | null;
  state: 'running' | 'complete' | 'error';
  directory: string;
  copied: number;
  skipped_existing: number;
  conflicts: string[];
  errors: string[];
  finished_at: number | null;
}

/** A run waiting its turn; positions count across every database. */
export interface WbppQueuedRun {
  id: string;
  scope: string;
  project_id: number | null;
  target_id: number | null;
  /** 1 is next. */
  position: number;
  queued_at: number;
}

/** A WBPP run under way, for the header's queue. */
export interface WbppActiveRun {
  db_id: string;
  db_name: string;
  scope: string;
  project_id?: number | null;
  target_id?: number | null;
  stage: string;
  wbpp_stage: string | null;
  wbpp_steps: number;
  started_at: number | null;
}

export interface WbppQueuedActivity extends WbppQueuedRun {
  db_id: string;
  db_name: string;
}

/** WBPP runs under way and waiting, on every database. */
export interface WbppActivity {
  running: WbppActiveRun[];
  /** The whole line, next first. */
  queued: WbppQueuedActivity[];
}

/** The route a job row's Stop and move buttons call. Mirrors `RowControl`
 *  in `src/server/jobs.rs`. */
export type JobControl =
  | { kind: 'stack'; job_id: string }
  | { kind: 'duplicates'; db_id: string }
  | { kind: 'rejects'; db_id: string }
  | { kind: 'backup' }
  | { kind: 'ts-sync'; job_id: string }
  | { kind: 'wbpp-running'; db_id: string }
  | { kind: 'wbpp-queued'; db_id: string; queue_id: string }
  | { kind: 'preview'; preview: PreviewKind; key: string }
  | { kind: 'import'; db_id: string }
  | { kind: 'export'; db_id: string }
  | { kind: 'catalog-install' };

/** The automatic jobs a person can pause. Mirrors `PausableJob` in
 *  `src/server/catalog_jobs.rs`. */
export type PausableJob = 'quality' | 'pregeneration' | 'flats' | 'sites';

/** The job a row's Pause or Resume button calls. Mirrors `RowPause` in
 *  `src/server/jobs.rs`. */
export interface JobPause {
  db_id: string;
  job: PausableJob;
  /** A person paused the job: the row shows Resume instead of Pause. */
  paused: boolean;
}

/** What a preview previews. Mirrors `PreviewKind` in
 *  `src/server/previews.rs`. */
export type PreviewKind = 'sync' | 'import' | 'export' | 'peer_sync' | 'incoming';

/** The view that shows what a job works on: `/{view}?db=…&project=…&target=…`,
 *  then `params` in order. */
export interface JobLink {
  view: 'grid' | 'sequence' | 'stacks' | 'calibration';
  db: string;
  project?: number;
  target?: number;
  params?: [string, string][];
}

/** One job in the header's list, as `GET /api/jobs` returns it. */
export interface JobRow {
  key: string;
  kind: 'refresh' | 'quality' | 'flats' | 'sites' | 'duplicates' | 'rejects' | 'stack' | 'processing' | 'wbpp' | 'transfer' | 'backup' | 'catalog' | 'grading' | 'health' | 'headers' | 'flat_use' | 'upkeep' | 'preview' | 'import' | 'export' | 'artifact_search' | 'planning_push' | 'pregeneration';
  title: string;
  scope: string;
  detail: string;
  status: 'attention' | 'running' | 'queued' | 'waiting' | 'paused';
  /** Automatic or database-wide work, listed after a person's work. */
  background: boolean;
  automatic: boolean;
  waiting_for?: string;
  percent: number | null;
  remaining?: string;
  hint?: string;
  queue?: 'stack' | 'wbpp';
  position?: number;
  control?: JobControl;
  stoppable: boolean;
  /** Pause or Resume, for automatic work a person can pause. */
  pause?: JobPause;
  state_label?: string;
  transfer?: import('./transferTypes').TransferJob;
  link?: JobLink;
  /** When a running processing run started, Unix seconds. */
  started_at?: number;
}

/** One database's state, for the header's effects. */
export interface DatabaseJobs {
  id: string;
  name: string;
  /** Whether work that changes the database's images, metrics or grades runs. */
  busy: boolean;
  /** Whether a quality scan or analysis runs. */
  scanning: boolean;
  /** The last quality scan's frame errors. */
  scan: {
    errors: number;
    last_error: string | null;
    target_id: number | null;
    project_id: number | null;
  };
}

export interface JobsResponse {
  jobs: JobRow[];
  databases: DatabaseJobs[];
}

export interface WbppRunStatus {
  /** POST: whether PixInsight started now. GET: whether a run is under way. */
  started: boolean;
  /** Set when a start was queued rather than started. */
  queue_id?: string | null;
  /** This database's runs waiting their turn. */
  queued: WbppQueuedRun[];
  progress: WbppRunProgress;
}

export interface StartWbppRunRequest {
  project_id?: number;
  target_id?: number;
  include_pending: boolean;
  filter_name?: string;
  options: WbppOptions;
  /** Further name=value WBPP parameters, passed as given. */
  extra_params?: string[];
  scope_label?: string;
  /** Where this run's folder goes, overriding the settings for one run. */
  work_root?: string;
  /** Save the masters to `<process_dir>/<folder>/master/` when the run finishes. */
  publish_folder?: string;
}

/** How much of AstroBin's acquisition CSV to fill in. */
export type AstroBinDetail = 'essentials' | 'full';

/**
 * Server-wide defaults: filter name, as the catalog spells it, to AstroBin
 * equipment id, used when a catalog's own map has no entry.
 */
export interface AstroBinSettings {
  filter_ids: Record<string, number>;
}

/**
 * One line of a catalog's filter map: what a filter name meant on this rig,
 * and when. Nights are `YYYY-MM-DD`; an absent bound is open.
 */
export interface AstroBinFilterEntry {
  id?: number;
  filter_name: string;
  astrobin_id: number;
  label?: string;
  from_night?: string;
  to_night?: string;
}

/** A filter name the catalog's lights carry, rejects aside, and the nights it spans. */
export interface AstroBinSeenFilter {
  filter_name: string;
  frames: number;
  first_night: string;
  last_night: string;
}

export interface AstroBinFilterMap {
  entries: AstroBinFilterEntry[];
  /** The catalog's filter names, for the map to offer. */
  seen: AstroBinSeenFilter[];
}

/** One row of the AstroBin CSV: one night, filter and exposure length. */
export interface AstroBinRow {
  date: string;
  /** The catalog's filter name; the CSV carries `filter_id` instead. */
  filter: string;
  filter_id: number | null;
  /** What the catalog's filter map calls that filter, when it says. */
  filter_label?: string;
  number: number;
  duration: number;
  binning: number | null;
  gain: number | null;
  sensor_cooling: number | null;
  f_number: number | null;
  darks: number | null;
  flats: number | null;
  flat_darks: number | null;
  bias: number | null;
  temperature: number | null;
}

export interface AstroBinExport {
  filename: string;
  detail: AstroBinDetail;
  /** The target or project the rows describe. */
  scope: string;
  rows: AstroBinRow[];
  csv: string;
  frames: number;
  nights: number;
  total_exposure_seconds: number;
  /** Filter names with no AstroBin id yet; their `filter` cell is blank. */
  unmapped_filters: string[];
  lights_missing_files: number;
  /** Lights left out because they can't be calibrated; `notes` says why. */
  uncalibrated_lights?: number;
  notes: string[];
}

export interface ServerInfo {
  version: string;
  cache_directory: string;
  /** Whether database mutations and sync are accepted on this server. */
  allow_database_management: boolean;
  /** Optional plain-text notice configured by the server administrator. */
  banner?: SiteBanner;
}

export type AccessRole = 'read_only' | 'read_write';

export interface AuthStatus {
  authentication_required: boolean;
  authenticated: boolean;
  role?: AccessRole;
  username?: string;
  can_compute: boolean;
}

/** The signed-in user's view preferences, as `/api/me/preferences` keeps
 *  them: the keys of `DisplayPreferences` the server knows. */
export interface AccountPreferences {
  schema_version: number;
  preferences: Record<string, unknown>;
  updated_unix_seconds?: number;
  /** Keys the server did not know, and so did not keep. */
  ignored?: string[];
}

export interface AuthUserSummary {
  username: string;
  role: AccessRole;
  email?: string;
}

export interface CreateAuthUserRequest {
  username: string;
  role: AccessRole;
  email?: string;
  password: string;
}

export interface UpdateAuthUserRequest {
  role: AccessRole;
  email?: string;
  password?: string;
}

/** A personal API token, minus its secret. */
export interface AuthTokenSummary {
  id: string;
  username: string;
  label: string;
  /** The access the token grants now: its user's role, narrowed by read_only. */
  role: AccessRole;
  read_only: boolean;
  /** Unix seconds. */
  created_at: number;
  /** Unix seconds; absent means the token does not expire. */
  expires_at?: number;
}

export interface CreateAuthTokenRequest {
  label: string;
  read_only?: boolean;
  expires_in_days?: number;
  /** Mint for another user; editors only. */
  username?: string;
}

/** The one response that carries a token's secret. */
export interface MintedAuthToken {
  token: string;
  summary: AuthTokenSummary;
  tokens: AuthTokenSummary[];
}

export interface ReleaseNotice {
  schema_version: number;
  version: string;
  release_url: string;
  summary?: string;
  urgency: 'normal' | 'recommended' | 'required';
  minimum_supported_version?: string;
  published_at?: string;
}

export interface UpdateNoticeStatus {
  notice?: ReleaseNotice;
  checking: boolean;
  checked_at_unix_seconds?: number;
}

/** One configured database, returned by /api/databases. */
export interface DatabaseSummary {
  ts_sync?: import('./tsSyncTypes').TsSyncSettings;
  id: string;
  name: string;
  database_path: string;
  image_directories: string[];
  remote_image_upload: RemoteImageUploadSummary;
  /**
   * Operator-configured server-side export destination. When present the
   * UI offers a server export that runs without database management.
   */
  export_directory?: string;
  /** Where this rig's finished work lives; a WBPP run's masters can be saved below it. */
  process_directory?: string;
  /** Automatic import of new frames, when turned on. */
  autoimport?: AutoImportSettings;
  /** What every import from the image folders leaves out, when set. */
  scan_rules?: ScanRules;
}

/**
 * What every import from a database's image folders leaves out. Patterns
 * match paths relative to the image folder; one with no `/` matches a file
 * or folder name at any depth.
 */
export interface ScanRules {
  exclude: string[];
  /** When any are set, only files matching one, or inside a folder matching one, are read. */
  include: string[];
  /** A frame with no IMAGETYP is left out instead of taken for a light. */
  lights_must_say_light: boolean;
  /** A light with no OBJECT is left out instead of becoming "Unknown Target". */
  require_object: boolean;
  /** Lights shorter than this, or of unknown length, are left out. */
  min_exposure_seconds?: number | null;
  /** Attach a light whose name matches no target to a target within 0.5°. On by default. */
  match_by_position: boolean;
}

export const DEFAULT_SCAN_RULES: ScanRules = {
  exclude: [],
  include: [],
  lights_must_say_light: false,
  require_object: false,
  min_exposure_seconds: null,
  match_by_position: true,
};

/** What a run does with lights that match no target in the catalog. */
export type NewTargets = 'create' | 'skip' | 'ask';

/** When and what an automatic import brings in. */
export interface AutoImportSettings {
  enabled: boolean;
  /** Run once when the server or desktop app opens the database. */
  on_open: boolean;
  /** Run again this many minutes after the last run; 0 means only on open. */
  interval_minutes: number;
  scope: ImportScope;
  /** Queue the background quality scan for the frames a run brings in. */
  backfill: boolean;
  accept_other_rigs: boolean;
  /** Lights that match no target: held for review (the default), left out, or made into projects. */
  new_targets: NewTargets;
}

export const DEFAULT_AUTOIMPORT_SETTINGS: AutoImportSettings = {
  enabled: false,
  on_open: true,
  interval_minutes: 0,
  scope: 'all',
  backfill: true,
  accept_other_rigs: false,
  new_targets: 'ask',
};

/** `GET /api/db/{db_id}/autoimport`. */
export interface AutoImportStatus {
  settings?: AutoImportSettings | null;
  /** Unix seconds of the last automatic run's start this process. */
  last_started_at?: number | null;
  /** Unix seconds when the next scheduled run is due. */
  next_run_at?: number | null;
  /** The import job when its last run was automatic. */
  progress?: ImportJobProgress | null;
}

/** What one export placed, and how. */
export interface ExportSummary {
  /** Stopped before every planned file was placed. */
  stopped?: boolean;
  planned: number;
  copied: number;
  linked: number;
  /** Copy-on-write clones: free like a hardlink, independent like a copy. */
  reflinked?: number;
  /** Symbolic links to the originals. */
  symlinked?: number;
  /** Left where they are and named in the WBPP runner. */
  referenced?: number;
  skipped_existing: number;
  missing: number;
  errors: number;
  bytes: number;
  /** Lights left out because they can't be calibrated, as stacks leave them out. */
  uncalibrated?: number;
  /** One of them and why: "acc_0001.fits: No matching flat". */
  uncalibrated_example?: string | null;
  /** Lights whose flats are from across a measured change, which WBPP may not calibrate as a stack would. */
  flat_warnings?: number;
  /** One of them and why. */
  flat_warning_example?: string | null;
}

/** Progress of the singleton per-DB server export job. */
export interface ExportJobProgress {
  running: boolean;
  stage: string;
  destination: string;
  scope: string;
  total_files: number;
  placed_files: number;
  outcome?: ExportSummary | null;
  started_at?: number | null;
  finished_at?: number | null;
  error?: string | null;
}

export interface ExportStatus {
  started: boolean;
  progress: ExportJobProgress;
}

export interface RemoteClientSummary {
  client_uuid: string;
  name: string;
  paired_at: number;
}

export type RemoteImageUploadPlacement = 'flat' | 'target_tree';
export type RemoteImageUploadDirectoryTemplateSource = 'catalog' | 'preset';

export interface RemoteImageUploadSummary {
  enabled: boolean;
  image_directory?: string;
  /** Server-owned layout below the configured receive directory. */
  placement?: RemoteImageUploadPlacement;
  /** Persisted fallback used when no catalog layout can be detected. */
  directory_template: string;
  /** Catalog-derived layout currently taking precedence over the fallback. */
  catalog_directory_template?: string;
  /** Whether the effective layout came from existing catalog paths or configuration. */
  directory_template_source: RemoteImageUploadDirectoryTemplateSource;
  /** Number of catalog paths supporting a detected layout. */
  directory_template_samples: number;
  token_configured: boolean;
  /** Remote scheduler sync is a separate grant from image upload. */
  sync_enabled: boolean;
  /** Paired clients; each holds its own revocable credential. */
  clients?: RemoteClientSummary[];
}

export type SchedulerSyncKind = 'pull' | 'push_planning' | 'push_grades';

export interface SchedulerSyncRequest {
  peer_db_id: string;
  kind: SchedulerSyncKind;
  dry_run?: boolean;
  with_image_data?: boolean;
  project?: string;
  target?: string;
  status?: 'pending' | 'accepted' | 'rejected';
  reviewed_only?: boolean;
}

export interface SchedulerSyncTableCounts {
  inserted: number;
  updated: number;
  unchanged: number;
  skipped: number;
  /** Rows deleted because the source catalog deleted their project and
   *  nothing was captured for it in the destination. Absent on older
   *  responses. */
  deleted?: number;
}

export interface SchedulerSyncGradeCounts {
  source_considered: number;
  source_no_guid: number;
  matched: number;
  changed: number;
  unchanged: number;
  unmatched_source: number;
  destination_only: number;
  duplicate_guids: number;
  transitions: Record<string, number>;
}

/** PSF Guard's calibration library as a full pull carries it. */
export interface SchedulerSyncCalibrationCounts {
  frames: SchedulerSyncTableCounts;
  grades: SchedulerSyncTableCounts;
  /** Frames the pull sets to Keep out. */
  kept_out: number;
  /** Frames the pull sets to Use anyway. */
  used_anyway: number;
  /** Frames whose choice the pull clears, leaving their grade to decide. */
  cleared: number;
  /** Splits and joins of flat groups the pull sets or resets. */
  flat_groups?: number;
  /** Lights whose flat side or group the pull sets or resets. */
  flat_lights?: number;
}

export interface SchedulerSyncResponse {
  kind: SchedulerSyncKind;
  dry_run: boolean;
  source_db_id: string;
  destination_db_id: string;
  exposuretemplate: SchedulerSyncTableCounts;
  project: SchedulerSyncTableCounts;
  ruleweight: SchedulerSyncTableCounts;
  target: SchedulerSyncTableCounts;
  exposureplan: SchedulerSyncTableCounts;
  acquiredimage: SchedulerSyncTableCounts | null;
  imagedata: SchedulerSyncTableCounts | null;
  grades: SchedulerSyncGradeCounts | null;
  grade_filled: number;
  grade_preserved: number;
  /** Local import/upload rows recognized as telescope frames and updated in place; absent on old previews. */
  adopted?: number;
  imagedata_bytes: number;
  total_inserted: number;
  total_updated: number;
  /** Per-entity change lines, capped server-side; absent on old previews. */
  changes?: string[];
  /** Present only for a full pull; absent on old previews. */
  calibration?: SchedulerSyncCalibrationCounts | null;
}

export interface SchedulerSyncPreviewResponse {
  preview_id: string;
  created_at: number;
  expires_at: number;
  result: SchedulerSyncResponse;
}

/** One staged transfer for a catalog, wherever it was created — the UI's
 * own preview flow or a remote client's push. */
export interface SchedulerSyncPreviewListEntry {
  preview_id: string;
  kind: string;
  source: string;
  created_at: number;
  expires_at: number;
  result: SchedulerSyncResponse;
}

/** Per-project line of an import outcome report. */
export interface ImportProjectSummary {
  name: string;
  targets: number;
  frames: number;
}

/** One existing target that received attached frames. */
export interface ImportAttachSummary {
  project: string;
  target: string;
  frames: number;
  /** 'name' or 'coordinates' */
  matched_by: string;
  /** Exposure plans the run adds to this target. */
  plans_created?: number;
  /** Existing plans of this target whose acquired count the run raises. */
  plans_raised?: number;
}

/** Result of one FITS import run (mirrors Rust `ImportOutcome`). */
export interface ImportOutcome {
  scanned: number;
  unreadable: number;
  non_light: number;
  /** Masters and calibrated/registered intermediates left out. */
  skipped_processed?: number;
  /** Frames outside the run's scope. */
  skipped_out_of_scope?: number;
  calibration: CalibrationImportOutcome;
  skipped_existing: number;
  /** Lights from a rig the catalog has not recorded, left out. */
  skipped_other_rig?: number;
  other_rigs?: ImportOtherRigSummary[];
  /** Files and folders the scan rules left out before reading a header. */
  scan_rules?: ScanRuleReport;
  /** Lights the scan rules' light filters left out. */
  skipped_by_filter?: number;
  left_out?: ImportLeftOutSummary[];
  /** What the run did with lights that match no existing target. */
  new_targets?: NewTargets;
  /** Lights left out or held because they match no existing target. */
  skipped_unmatched?: number;
  unmatched?: ImportUnmatchedSummary[];
  /**
   * Lights left out or held because their target, in a project PSF Guard did
   * not create, has no exposure plan for them.
   */
  skipped_unplanned?: number;
  unplanned?: ImportUnplannedSummary[];
  /** Existing exposure plans whose acquired count the run raises. */
  plans_raised?: number;
  /**
   * Renamed files that took over the frame catalogued under their old name,
   * whose file was gone, instead of becoming a second frame.
   */
  renamed?: number;
  imported: number;
  /** Frames attached to targets that already existed. */
  attached: number;
  projects_created: number;
  targets_created: number;
  templates_created: number;
  templates_reused: number;
  plans_created: number;
  profile_id: string;
  dry_run: boolean;
  project_summaries: ImportProjectSummary[];
  attach_summaries: ImportAttachSummary[];
  created_target_ids: number[];
  attached_target_ids: number[];
}

export interface ScanRuleReport {
  /** Image files an exclude or include pattern left out. */
  files_skipped: number;
  /** Folders an exclude pattern left out, with everything below. */
  folders_skipped: number;
  /** Folders a `.psfguardignore` file left out, with everything below. */
  ignored_folders: number;
}

export type ImportLeftOutReason = 'no_image_type' | 'no_target_name' | 'short_exposure';

/** Lights one light filter left out. */
export interface ImportLeftOutSummary {
  reason: ImportLeftOutReason;
  frames: number;
  example: string;
}

/** Lights for an existing target that has no exposure plan for them. */
export interface ImportUnplannedSummary {
  project: string;
  target: string;
  filter?: string | null;
  exposure_s?: number | null;
  frames: number;
  folders: string[];
}

/** Lights of one target name that match nothing in the catalog. */
export interface ImportUnmatchedSummary {
  /** The frames' OBJECT; absent when they have none. */
  target?: string | null;
  frames: number;
  folders: string[];
}

/** Frames from one rig the catalog does not know, left out of a run. */
export interface ImportOtherRigSummary {
  rig: string;
  frames: number;
  example: string;
}

export interface CalibrationImportOutcome {
  imported: number;
  updated: number;
  skipped_existing: number;
  bias: number;
  dark: number;
  dark_flat: number;
  flat: number;
}

export interface CalibrationRigSummary {
  rig_uuid: string;
  name: string;
  profile_id?: string | null;
  /** Every N.I.N.A. profile whose frames went into this rig. */
  profile_ids?: string[];
  telescope?: string | null;
  camera?: string | null;
  frame_count: number;
  bias: number;
  dark: number;
  dark_flat: number;
  flat: number;
  oldest_at?: number | null;
  newest_at?: number | null;
  /** The rig's clock over time: its frames' and masters' times read on it. */
  clock?: FlatClockRun[];
}

export interface CalibrationLibrarySummary {
  schema_version: number;
  frame_count: number;
  master_count: number;
  rigs: CalibrationRigSummary[];
}

export interface CalibrationFrameSummary {
  frame_uuid: string;
  rig_uuid: string;
  kind: 'bias' | 'dark' | 'dark_flat' | 'flat';
  source_path: string;
  source_exists: boolean;
  captured_at?: number | null;
  camera?: string | null;
  width?: number | null;
  height?: number | null;
  binning_x?: number | null;
  binning_y?: number | null;
  gain?: number | null;
  offset?: number | null;
  readout_mode?: number | null;
  /** The readout mode as N.I.N.A. names it, when the header spelled it that way. */
  readout_mode_name?: string | null;
  /** An integrated master from other software, used as a master as-is. */
  is_master?: boolean;
  bayer_pattern?: string | null;
  exposure_s?: number | null;
  camera_temp?: number | null;
  filter?: string | null;
  focal_length_mm?: number | null;
  /** Rotator angle in degrees at capture, when the rig recorded one. */
  rotation?: number | null;
  /**
   * User-set validity boundary: `forward` serves only lights captured at or
   * after this frame, `backward` only at or before. Absent means both.
   */
  valid_direction?: CalibrationValidDirection | null;
  /** Median level of a raw dark or dark-flat, once measured. */
  dark_level?: number | null;
  /** Spread of that dark's pixels around the median, measured with it. */
  dark_level_sigma?: number | null;
  /**
   * The stray-light mark on a dark whose level sits well above the matching
   * darks. Older builds read only this.
   */
  stray_light?: string | null;
  /** The frame's grade once measured against its peers. */
  grade?: CalibrationGrade | null;
  /** The main finding's code, such as `level_excess` or `noise`. */
  grade_code?: string | null;
  /** The main finding in plain words, with its number. */
  grade_reason?: string | null;
  /** A person's choice, which outranks the grade. */
  quality_override?: CalibrationQualityOverride | null;
  /** Why the frame stays out of masters and exports, when it does. */
  kept_out?: string | null;
}

export type CalibrationGrade = 'ok' | 'suspect' | 'poor';

export type CalibrationKind = CalibrationFrameSummary['kind'];

/** A frame in the browser's lists: its night and the session it was shot in. */
export interface BrowsedCalibrationFrame extends CalibrationFrameSummary {
  /** `YYYY-MM-DD`; absent without a capture time. */
  night?: string | null;
  session: string;
  /** Whether it can be shown against its session, as its detail says. */
  compare?: boolean;
}

export interface CalibrationFramePage {
  frames: BrowsedCalibrationFrame[];
  total: number;
  page: number;
  per_page: number;
}

/** Frames of one kind from one night with one set of capture settings. */
export interface CalibrationSession {
  id: string;
  kind: CalibrationKind;
  night: string | null;
  rig_uuid: string;
  rig_name: string | null;
  is_master: boolean;
  filter: string | null;
  exposure_min_s: number | null;
  exposure_max_s: number | null;
  temp_min_c: number | null;
  temp_max_c: number | null;
  gain: number | null;
  offset: number | null;
  readout: string | null;
  binning: string | null;
  width: number | null;
  height: number | null;
  rotation: number | null;
  first_at: number | null;
  last_at: number | null;
  /** Frames the filters keep; the counts are of these. */
  frames: number;
  ok: number;
  suspect: number;
  poor: number;
  ungraded: number;
  kept_out: number;
}

/** Values the filters can take, across the library. */
export interface CalibrationFacets {
  filters: string[];
  exposures: number[];
  first_night?: string | null;
  last_night?: string | null;
  temp_min_c?: number | null;
  temp_max_c?: number | null;
  /** The flats' rotator angles, whole degrees, one per angle. */
  rotations?: number[];
}

export interface CalibrationSessionPage {
  sessions: CalibrationSession[];
  total: number;
  page: number;
  per_page: number;
  frames: number;
  facets: CalibrationFacets;
  night_boundary_seconds: number;
  /** Asked for the page holding one session (`locate`): whether the
   *  filters keep it. When they do not, this is the first page. */
  located?: boolean;
}

export type CalibrationGradeFilter = CalibrationGrade | 'ungraded';
export type CalibrationFrameSort = 'newest' | 'oldest' | 'file' | 'grade' | 'night';

/** What the frame and session lists keep. */
export interface CalibrationListFilters {
  rig?: string;
  kind?: CalibrationKind;
  filter?: string;
  exposure?: number;
  temp_min?: number;
  temp_max?: number;
  night_from?: string;
  night_to?: string;
  grade?: CalibrationGradeFilter;
  kept_out?: boolean;
  missing?: boolean;
  /** A rotator angle in degrees, matched within 2°. */
  rotation?: number;
  session?: string;
  /** Keep only the sessions this project's lights use. */
  project?: number;
  sort?: CalibrationFrameSort;
  page?: number;
  per_page?: number;
  /** Sessions only: the page holding this session, in place of `page`. */
  locate?: string;
  /** Frames only: a flat group's flats, by the group's key or one of its
   *  sessions; night by night, in one page of up to 5,000. */
  group?: string;
}

/** A calibration rig in a project's scope. */
export interface CalibrationScopeRig {
  rig_uuid: string;
  name: string | null;
}

/** What one project's lights use from the calibration library. */
export interface CalibrationProjectScope {
  project_id: number;
  /** Lights matched against the library. */
  lights: number;
  /** Lights whose file was not found or whose header could not be read. */
  unread: number;
  /** The Calibration page's sessions holding frames the lights use. */
  sessions: string[];
  frames: number;
  /** Recorded masters the lights calibrate with. */
  masters: string[];
  rigs: CalibrationScopeRig[];
  /** Lights whose headers are still being read; the scope grows as they arrive. */
  pending: number;
  /** What the scope was worked out from; the lists ask again when it changes. */
  revision: string;
  /** Worked out before the latest change; a newer one is on its way. */
  refreshing: boolean;
}

export interface CalibrationFrameMetric {
  key: string;
  value: number;
  /** The median among the other measured frames of its session. */
  session: number | null;
}

/** A limit the frame crossed, with the numbers behind it. */
export interface CalibrationFinding {
  code: string;
  grade: 'suspect' | 'poor';
  value: number;
  limit: number;
  text: string;
}

/** What a dust shadow looks like. */
export type DustShape = 'compact' | 'irregular' | 'disk' | 'donut' | 'patch';

/** One dust feature of a flat, in sensor pixels: a compact or irregular dip,
 *  an out-of-focus disk or donut, or a patch of overlapping ones. */
export interface CalibrationFlatFeature {
  x: number;
  y: number;
  left: number;
  top: number;
  right: number;
  bottom: number;
  pixels: number;
  /** As a fraction of the light around it. */
  depth: number;
  /** Measurements made before shapes were reported have none: compact. */
  shape?: DustShape;
  /** A disk's or donut's rim; a dip's or patch's, of a disc of its area. */
  radius?: number;
  /** A donut's hole, as a fraction of `radius`. */
  hole?: number;
  /** How far in front of the sensor the dust sits, in millimetres. */
  distance_mm?: number;
  /** In how many of its session's flats it shows. */
  seen_in?: { frames: number; of: number };
}

/** Which part of the border a vignetting shadow hangs from. */
export type VignettingEdge =
  | 'top' | 'bottom' | 'left' | 'right'
  | 'top_left' | 'top_right' | 'bottom_left' | 'bottom_right';

/** A large, smooth shadow hanging from the border: not dust. */
export interface CalibrationVignetting {
  edge: VignettingEdge;
  x: number;
  y: number;
  area: number;
  depth: number;
}

/** A flat's session dust, found on the stack of the session's flats. */
export interface CalibrationSessionDust {
  /** Sensor pixels per pixel of the stack. */
  bin: number;
  frames: number;
  features: CalibrationFlatFeature[];
  vignetting: CalibrationVignetting[];
  /** Indices into `features` this flat shows. */
  seen: number[];
  /** What this flat holds that the stack does not: not dust. */
  transients: CalibrationFlatFeature[];
}

export interface CalibrationFrameDetail extends BrowsedCalibrationFrame {
  measurement: {
    type: 'flat' | 'dark';
    width: number;
    height: number;
    features?: CalibrationFlatFeature[];
    feature_count?: number;
    vignetting?: CalibrationVignetting[];
  } | null;
  measure_error: string | null;
  findings: CalibrationFinding[];
  metrics: CalibrationFrameMetric[];
  session_frames: number;
  session_measured: number;
  /** A flat with enough flats in its session to be compared with them. */
  compare: boolean;
  /** Recorded masters built from this frame. */
  masters: number;
  /** A flat's session dust, once the flat grouping pass measured it. */
  session_dust?: CalibrationSessionDust;
}

/** How well the library calibrates a project: lights that cannot be
 *  calibrated properly, or ones worth a look. */
export type CalibrationHealthLevel = 'poor' | 'check';

export interface ProjectCalibrationHealthSummary {
  project_id: number;
  /** Absent when nothing was found. */
  level: CalibrationHealthLevel | null;
  poor_lights: number;
  check_lights: number;
  checked: number;
  computed_at: number;
}

export interface CalibrationHealthList {
  /** Projects with a current result; one missing has none yet. */
  projects: ProjectCalibrationHealthSummary[];
  running: boolean;
}

/** The rule a calibration health finding comes from. */
export type CalibrationHealthCode =
  | 'no_library'
  | 'cannot_calibrate'
  | 'thin_dark'
  | 'thin_bias'
  | 'flat_spots'
  | 'old_flats'
  | 'mixed_flats'
  | 'suspect_frames'
  | 'short_after_keep_out'
  | 'flats_differ';

export interface CalibrationHealthFinding {
  code: CalibrationHealthCode;
  level: CalibrationHealthLevel;
  /** In plain words, with its numbers. */
  reason: string;
  lights: number;
  /** Newest night first. */
  nights: { night: string; filter: string; lights: number }[];
  /** The lights themselves. */
  image_ids?: number[];
}

/** A light the calibration library serves poorly, or one worth a look. */
export interface CalibrationLightMark {
  image_id: number;
  /** Its worst finding's level. */
  level: CalibrationHealthLevel;
  codes: CalibrationHealthCode[];
}

export interface CalibrationLightMarks {
  /** Lights with a finding. One not listed has none, or no result yet. */
  lights: CalibrationLightMark[];
  /** Projects whose result came before a change; the next pass replaces it. */
  stale_projects: number[];
  running: boolean;
}

/** One thing wrong with one light's calibration. */
export interface LightCalibrationFinding {
  code: CalibrationHealthCode;
  level: CalibrationHealthLevel;
  /** In plain words, with its numbers. */
  reason: string;
  /** The kinds of calibration frame it is about. */
  kinds?: CalibrationKind[];
}

/** Frames of one night, with the coldest and warmest sensor reading. */
export interface CalibrationNightFrames {
  night: string;
  frames: number;
  temp_min_c?: number;
  temp_max_c?: number;
}

/** A named frame and why it is called out. */
export interface CalibrationNamedFrame {
  file: string;
  night: string;
  reason: string;
}

/** For one kind, what the library gives a light and what it passes over. */
export interface CalibrationKindExplanation {
  kind: CalibrationKind;
  /** The frames the master would take, in sum. */
  master: {
    frames: number;
    temp_min_c?: number;
    temp_max_c?: number;
    first_night?: string;
    last_night?: string;
    nights: number;
  };
  /** The nights whose frames the master takes, newest first. */
  used: CalibrationNightFrames[];
  /** Frames that match, but another set was taken instead. */
  matching_unused: CalibrationNightFrames[];
  /** Frames that do not match, by night and reason. */
  refused: { night: string; frames: number; reason: string }[];
  /** Matching frames graded poor or kept out by hand. */
  kept_out: CalibrationNamedFrame[];
  /** Frames the master takes though they are graded suspect. */
  suspect: CalibrationNamedFrame[];
  other_filters: number;
  other_cameras: number;
  /** Flats only: how its flats were chosen around a measured change, in a sentence. */
  chosen_by?: string;
  /** Flats only: absent when the nearest flats were taken and no change was involved. */
  basis?: FlatBasis;
  /** Flats only: the master mixes nights measured alike. Fractions, not percent. */
  alike?: { nights: number; dust: number; broad: number };
  /** Flats only: the response family they come from, when measured grouping
   *  chose one at another angle than the light's, or by its own pixels. */
  family?: FlatFamilyNote;
  /** Flats only: a person's choices for this light, in force or orphaned. */
  choices?: FlatChoiceNote[];
  /** Flats only, with measured grouping: what a person can choose. */
  choice_options?: FlatChoiceOptions;
}

/** A person's choice for one light's flats. */
export interface FlatChoiceNote {
  choice: 'before' | 'after' | 'group';
  /** The change it is about, as recorded; absent for a group. */
  change_key?: string;
  /** What it was about, in words. */
  label: string;
  chosen_at: number;
  /** Why it does not apply now: its change or flats are gone. It stays until reset. */
  orphaned?: string;
}

/** What a person can choose for one light's flats. */
export interface FlatChoiceOptions {
  /** Changes it can be put on a side of: its window holds the light, its
   *  flats were chosen around it, or a person chose a side. */
  changes: FlatChangeOption[];
  /** Groups its flats can come from, the one it takes now first. */
  groups: FlatGroupOption[];
}

export interface FlatChangeOption {
  key: string;
  start: number;
  end: number;
  what: string;
  camera_side: boolean;
  filters: string[];
  /** Where its time, session or pixels put the light. */
  side: 'before' | 'after' | null;
  by: 'time' | 'session' | 'pixels' | 'undetermined' | 'person';
  /** The side a person chose. */
  chosen: 'before' | 'after' | null;
  /** The light was shot inside the change's window. */
  inside: boolean;
}

export interface FlatGroupOption {
  /** Its first session's response key: what a choice names. */
  response_key: string;
  family: string;
  first_night: string;
  last_night: string;
  sessions: number;
  /** The light's flats come from it now. */
  current: boolean;
  /** A person chose it. */
  chosen: boolean;
}

/** The response family a light's flats come from, and how it was chosen. */
export interface FlatFamilyNote {
  key: string;
  /** Filter, as the flats name it, and measured angle range: "OIII at
   *  72.6° & 82.2°". */
  label: string;
  /** `angle`: the only family near it, at its recorded angle; `pixels`:
   *  its own lights follow these flats; `nearest`: the family at its
   *  recorded angle, its lights not read or unable to tell; `disputed`:
   *  its lights pointed to another family and nothing confirmed it. */
  by: 'angle' | 'pixels' | 'nearest' | 'disputed' | 'person';
  /** `pixels` and `disputed`: how many lights, of which targets, said so. */
  lights?: number;
  targets?: string[];
  /** `pixels`: whether they moved it off its recorded angle, and what
   *  confirmed the move ("its OIII lights", "the rotator's epochs"). */
  moved?: boolean;
  corroborated?: string | null;
  /** `nearest`: its lights were read and could not tell. */
  undecided?: boolean;
  /** `disputed`: the family its lights pointed to. */
  pointed?: string;
  /** Whether the family has flats at the light's recorded angle. */
  at_recorded: boolean;
  light_angle: number;
  /** How far the rotator's epochs turn the light's angle into the flats'. */
  shift: number;
  /** The families within the angle prior, nearest first. */
  candidates: string[];
  /** When its master's flats lie at another angle inside a span the
   *  family's flats measure alike across: the span and its evidence. */
  alike_range?: FlatAlikeRangeNote;
}

/** A span of angles a flat family's own flats measure alike across. */
export interface FlatAlikeRangeNote {
  /** The angle of the flats the light's master is built from. */
  flats_angle: number;
  low: number;
  high: number;
  /** The angles at its two ends: "101.5–102.2° and 104.2–104.4°". */
  label: string;
  pairs: number;
  nights: number;
  /** Its pairs were all measured in other groups of the family, across
   *  camera-side dust changes only. */
  carried: boolean;
}

/** A measured change near a light, as its account names it. Fractions. */
export interface FlatEventNote {
  key: string;
  start: number;
  end: number;
  dust: number;
  broad: number;
  camera_side: boolean;
  filters: string[];
  what: string;
  /** A split a person made where the flats measure alike. */
  by_hand?: boolean;
}

/** Which side of a change a light was put on, and by what. */
export type FlatPlacement =
  | { by: 'time'; side: 'before' | 'after' }
  | { by: 'session'; side: 'before' | 'after'; start: number; end: number }
  | { by: 'pixels'; side: 'before' | 'after'; targets: string[]; filters: string[]; lights: number }
  | { by: 'undetermined'; session: [number, number] | null; pixels: string | null }
  | { by: 'person'; side: 'before' | 'after' };

/** How a light's flats were chosen around a measured change. */
export type FlatBasis =
  | { basis: 'same_side'; event: FlatEventNote; placement: FlatPlacement; left_out: number }
  | {
      basis: 'other_angle';
      event: FlatEventNote;
      placement: FlatPlacement;
      rotation: number | null;
      angle_term: { pairs: number; dust: number; broad: number };
      /** The rotation tolerance the stacker holds this light to, wide enough for those flats. */
      rotation_tolerance_deg: number | null;
    }
  | {
      basis: 'corrected';
      event: FlatEventNote;
      placement: FlatPlacement;
      correction: {
        event: string;
        before_response: string;
        after_response: string;
        light_side: 'before' | 'after';
        key: string;
      };
    }
  | { basis: 'no_same_side'; event: FlatEventNote; placement: FlatPlacement }
  | { basis: 'undetermined'; event: FlatEventNote; placement: FlatPlacement }
  | {
      basis: 'group';
      group: { family: string; first_night: string; last_night: string; sessions: number };
    };

/** Why one light got the calibration it did, and what is wrong with it. */
export interface ImageCalibrationExplanation {
  light: {
    file: string;
    night: string | null;
    filter: string | null;
    exposure_s: number | null;
    gain: number | null;
    offset: number | null;
    binning: string | null;
    camera_temp: number | null;
    rotator_deg: number | null;
    camera: string | null;
    telescope: string | null;
    focal_length_mm: number | null;
  };
  kinds: CalibrationKindExplanation[];
  /** Poor first. */
  findings: LightCalibrationFinding[];
  /** What a check would have found had a setting not turned it off. */
  waived?: LightCalibrationFinding[];
  /** The rules the light was matched and checked with. */
  rules?: LightCalibrationRules;
}

/** One setting that differs from its default for a light, and who set it. */
export interface CalibrationRuleNote {
  key: string;
  label: string;
  value: CalibrationRuleValue;
  /** The value in words. */
  text: string;
  source: 'default' | 'global' | 'rig';
}

export interface LightCalibrationRules {
  /** The light's rig, by the key its own settings are kept under. */
  rig_uuid: string;
  rig_name: string;
  rig_has_settings: boolean;
  notes: CalibrationRuleNote[];
}

export interface ProjectCalibrationHealth {
  project_id: number;
  level: CalibrationHealthLevel | null;
  checked: number;
  missing_files: number;
  poor_lights: number;
  check_lights: number;
  findings: CalibrationHealthFinding[];
}

export interface ProjectCalibrationHealthResponse {
  /** Absent until the background pass has judged the project as it is. */
  health: ProjectCalibrationHealth | null;
  computed_at: number | null;
  running: boolean;
}

export type CalibrationQualityOverride = 'use' | 'exclude';

export type CalibrationValidDirection = 'forward' | 'backward';

export interface CalibrationMutationOutcome {
  frames_removed: number;
  masters_removed: number;
}

/** One catalog row of an exposure the library records more than once. */
export interface CalibrationTwinMember {
  frame_uuid: string;
  source_path: string;
  /** Whether its file is on disk. */
  exists: boolean;
  /** Whether it is the row a repair keeps. */
  keep: boolean;
}

/** Rows that record one exposure, and what a repair makes of them. */
export interface CalibrationTwinGroup {
  kind: CalibrationKind;
  captured_at: number | null;
  file_name: string;
  rig_uuid: string;
  rig_name: string | null;
  /** The uuid of the row kept: the oldest. */
  keep: string;
  /** Its file after the repair. */
  path: string;
  /** Whether the kept row moves to a twin's file, its own being gone. */
  repointed: boolean;
  /** Whether more than one of the files is on disk. */
  physical: boolean;
  members: CalibrationTwinMember[];
}

export interface CalibrationInvalidatedMaster {
  master_uuid: string;
  kind: CalibrationKind;
  source_count: number;
  created_at: number;
  /** Cites a merged row itself, rather than a master that does. */
  direct: boolean;
  /** Took more than one copy of an exposure. */
  doubled: boolean;
}

/** The Duplicates filter's groups and the repair they preview. */
export interface CalibrationDuplicateReport {
  groups: CalibrationTwinGroup[];
  rows_removed: number;
  missing_rows: number;
  physical_rows: number;
  repointed: number;
  masters: CalibrationInvalidatedMaster[];
  /** Names this plan; Apply is refused once the library plans otherwise. */
  token: string;
}

export interface CalibrationRepairOutcome {
  groups: number;
  rows_removed: number;
  repointed: number;
  masters_removed: number;
}

/** How the plan a repair found differs from the plan previewed. */
export interface CalibrationPlanChange {
  groups_before: number;
  groups_after: number;
  rows_before: number;
  rows_after: number;
  /** Exposures the new plan repairs that the preview did not, by file name
   *  (the first 20); the count says how many in all. */
  added: string[];
  added_count: number;
  /** Exposures the preview repaired that the new plan leaves. */
  dropped: string[];
  dropped_count: number;
  /** Exposures in both whose rows or files differ. */
  altered: string[];
  altered_count: number;
}

/** The catalog's duplicate check or repair: a job, polled while it runs. */
export interface CalibrationDuplicateStatus {
  /** `idle` before the first run. */
  state: 'idle' | 'running' | 'done' | 'stopped' | 'failed';
  /** What the run is: `Checking for duplicates` or `Repairing duplicates`. */
  title: string;
  /** The stage and count, such as `Merging 37 of 203`. */
  label: string;
  /** 0–100 for the stage, or null before its size is known. */
  percent: number | null;
  action: 'check' | 'repair' | null;
  /** `checking_files`, `comparing_copies` or `merging`, while it runs. */
  stage: string | null;
  done: number;
  total: number;
  /** Counts up with each run, so a page knows which result is its own. */
  run: number;
  started_at: number | null;
  finished_at: number | null;
  /** The latest plan. None after a repair applied, until the next check. */
  report: CalibrationDuplicateReport | null;
  checked_at: number | null;
  /** The last repair that applied, and its run. */
  repaired: { run: number; finished_at: number; outcome: CalibrationRepairOutcome } | null;
  /** A repair that found the plan changed: what changed; `report` is the
   *  new plan. */
  changed: CalibrationPlanChange | null;
  error: string | null;
  /** `interactive` for a run a person asked for, `background` for the
   *  check a page starts on its own; while it runs. */
  priority?: 'interactive' | 'background' | null;
  /** What a person started that a background check waits for. */
  waiting_for?: string | null;
  /** The catalog's calibration rows changed since the plan was made. */
  stale?: boolean;
}

/** Progress of the singleton per-DB import job (poll ~1s while running). */
export interface ImportJobProgress {
  running: boolean;
  /** Whether the run is a preview, which writes nothing. A newer preview,
   *  or an import, replaces it. */
  preview?: boolean;
  /** scanning | importing | complete | error | "" (never ran) */
  stage: string;
  image_dirs: string[];
  total_files: number;
  scanned_files: number;
  outcome?: ImportOutcome | null;
  started_at?: number | null;
  finished_at?: number | null;
  error?: string | null;
  /** `manual` for the Import button and API, `automatic` for a scheduled or on-open run. */
  trigger?: string;
  /** Files an automatic run dropped as already cataloged, without a header read. */
  prefiltered?: number;
}

export interface ImportStatus {
  /** POST: whether this request started a job. GET: whether one is running. */
  started: boolean;
  progress: ImportJobProgress;
}

/** Body of `POST /api/db/{db_id}/import`. */
export interface ImportRequest {
  image_dirs?: string[];
  time_gap_days?: number;
  profile_id?: string;
  dry_run?: boolean;
  backfill?: boolean;
  /** Let the queued quality job write star count/HFR into imported images'
   *  metadata (missing keys only; default true). */
  fill_metadata?: boolean;
  /** Attach to existing targets by name/coordinates (default true). */
  attach_existing?: boolean;
  match_radius_deg?: number;
  /** Which frame kinds to import (default all). */
  scope?: ImportScope;
  /** Leave processing artifacts out of the catalog (default false). */
  skip_processed?: boolean;
  /** Take lights from a rig the catalog has not recorded (default false). */
  accept_other_rigs?: boolean;
}

/** How the calibration library covers one project (mirrors Rust
 *  `ProjectCalibrationReport`). */
export interface ProjectCalibrationReport {
  nights: CalibrationNightReport[];
  kinds: CalibrationKindSummary[];
  warnings: string[];
  lights_missing_files: number;
  /** The project's rejected lights, counted whether or not the report includes them. */
  rejected_lights: number;
  rejected_included: boolean;
  /** Where this catalog's nights split, in seconds after 00:00 UTC. */
  night_boundary_seconds: number;
}

export interface CalibrationKindSummary {
  kind: string;
  matching_frames: number;
  sessions: string[];
  newest_at?: number | null;
  oldest_at?: number | null;
}

export interface CalibrationNightReport {
  night: string;
  lights: number;
  filters: CalibrationNightFilter[];
}

export interface CalibrationNightFilter {
  filter: string;
  lights: number;
  bias_frames: number;
  dark_frames: number;
  dark_age_days?: number | null;
  dark_flat_frames: number;
  flat_frames: number;
  flat_session?: string | null;
  flat_age_days?: number | null;
  nightly_flats: boolean;
  missing: string[];
  /** What one dark master would build from, and the nights it spans. */
  dark_master_frames?: number;
  dark_master_nights?: number;
  /** With no flat, the nearest one refused only for its rotator angle. */
  flat_near_miss?: CalibrationFlatNearMiss | null;
  /** Masters from other software for this camera, sensor and filter. */
  external_masters?: CalibrationExternalMaster[];
  /** Why this night's lights cannot be calibrated properly. */
  cannot_calibrate?: string | null;
}

/** One light a stack leaves out because it cannot be calibrated. */
export interface UncalibratedLight {
  image_id: number;
  filter: string;
  night: string;
  reason: string;
}

/** Every light of a project that cannot be calibrated, ready to reject. */
export interface ProjectCalibrationGaps {
  lights: UncalibratedLight[];
  checked: number;
  missing_files: number;
  digest: string;
  /** Where this catalog's nights split, in seconds after 00:00 UTC. */
  night_boundary_seconds: number;
}

export interface CalibrationFlatNearMiss {
  filter: string | null;
  flat_rotation_deg: number;
  light_rotation_deg: number;
  off_by_deg: number;
  tolerance_deg: number;
  session: string | null;
  frames: number;
}

export interface CalibrationExternalMaster {
  kind: 'bias' | 'dark' | 'flat';
  file: string;
  matches: boolean;
  used: boolean;
  reason: string | null;
}

/** Which frames an import run touches. */
export type ImportScope = 'all' | 'lights' | 'calibration';

/** One directory in the import folder listing (two levels deep). */
export interface ImportFolder {
  path: string;
  name: string;
  children: ImportFolder[];
}

/** Body of `POST /api/databases/create`. */
export interface CreateDatabaseRequest {
  name: string;
  image_dirs: string[];
  db_path?: string;
  slug?: string;
  time_gap_days?: number;
  profile_id?: string;
  backfill?: boolean;
  /** Let the queued quality job write star count/HFR into imported images'
   *  metadata (missing keys only; default true). */
  fill_metadata?: boolean;
}

export interface CreateDatabaseResponse {
  database: DatabaseSummary;
  import: ImportJobProgress;
}

export interface FileCheckResponse {
  images_checked: number;
  files_found: number;
  files_missing: number;
  check_time_ms: number;
}

export interface DirectoryTreeResponse {
  total_files: number;
  unique_filenames: number;
  total_directories: number;
  age_seconds: number;
  build_time_ms: number;
  root_directory: string;
}

export const GradingStatus = {
  Pending: 0,
  Accepted: 1,
  Rejected: 2,
} as const;

export type GradingStatus = typeof GradingStatus[keyof typeof GradingStatus];

// Overview types
export interface DateRange {
  earliest?: number;
  latest?: number;
  span_days?: number;
}

export interface ProjectOverview {
  id: number;
  /** Target Scheduler's project GUID; the same value in two databases is one plan shot by two rigs. */
  guid?: string | null;
  profile_id: string;
  profile_name: string;
  name: string;
  display_name: string;
  description?: string;
  has_files: boolean;
  state: number;
  target_count: number;
  total_images: number;
  accepted_images: number;
  rejected_images: number;
  pending_images: number;
  total_desired: number;
  files_found: number;
  files_missing: number;
  date_range: DateRange;
  filters_used: string[];
  recent_images: ProjectRecentImage[];
}

export interface ProjectRecentImage {
  id: number;
  project_id: number;
  target_id: number;
  target_name: string;
  acquired_date: number | null;
  filter_name: string;
  grading_status: number;
}

export interface TargetOverview {
  id: number;
  name: string;
  ra?: number;
  dec?: number;
  active: boolean;
  project_id: number;
  project_name: string;
  image_count: number;
  accepted_count: number;
  rejected_count: number;
  pending_count: number;
  total_desired: number;
  files_found: number;
  files_missing: number;
  has_files: boolean;
  date_range: DateRange;
  filters_used: string[];
  coordinates_display?: string;
}

export interface ExposurePlanDetails {
  id: number;
  exposure_template_id: number;
  template_name: string;
  filter_name: string;
  gain: number | null;
  offset: number | null;
  bin: number | null;
  readout_mode: number | null;
  exposure: number;
  desired: number;
  acquired: number;
  accepted: number;
  enabled: boolean;
}

export interface ExposureTemplateDetails {
  id: number;
  profile_id: string;
  name: string;
  filter_name: string;
  gain: number | null;
  offset: number | null;
  bin: number | null;
  readout_mode: number | null;
  twilight_level: number;
  moon_avoidance_enabled: boolean;
  moon_avoidance_separation: number;
  moon_avoidance_width: number;
  maximum_humidity: number;
  default_exposure: number;
  moon_relax_scale: number;
  moon_relax_max_altitude: number;
  moon_relax_min_altitude: number;
  moon_down_enabled: boolean;
  dither_every: number;
  minutes_offset: number;
  plan_count: number;
}

export interface SchedulerTargetDetails {
  id: number;
  name: string;
  active: boolean;
  ra_hours: number;
  dec_degrees: number;
  epoch_code: number;
  rotation: number;
  roi: number;
  exposure_plans: ExposurePlanDetails[];
}

export interface ProjectSchedulerDetails {
  id: number;
  profile_id: string;
  name: string;
  description: string | null;
  state: number;
  priority: number;
  created_at: number | null;
  active_at: number | null;
  inactive_at: number | null;
  minimum_time: number;
  minimum_altitude: number;
  maximum_altitude: number;
  use_custom_horizon: boolean;
  horizon_offset: number;
  meridian_window: number;
  filter_switch_frequency: number;
  dither_every: number;
  enable_grader: boolean;
  is_mosaic: boolean;
  /** Target Scheduler flats automation: 0 off, 1-7 every N sessions,
   * 100 target completion, 200 immediate. */
  flats_handling: number;
  exposure_templates: ExposureTemplateDetails[];
  targets: SchedulerTargetDetails[];
}

/** What deleting a project would take from its catalog, from
 *  `GET /api/db/{db}/projects/{id}/removal`. */
export interface ProjectRemovalPreview {
  project_id: number;
  name: string;
  guid: string | null;
  /** Target Scheduler's state: 0 draft, 1 active, 2 inactive, 3 closed. */
  state: number | null;
  targets: number;
  exposure_plans: number;
  frames: { total: number; accepted: number; rejected: number; pending: number };
  /** Frames reported to a collaboration; any keeps the project. */
  protected_frames: number;
  /** Send back with the delete; it changes when a target, plan or frame does. */
  digest: string;
  linked_plan: { name: string; databases: number; from_collaboration: boolean } | null;
  /** The Target Scheduler database this catalog copies from. */
  synced_from: string | null;
  /** Database management is on, so the server deletes projects. */
  allowed: boolean;
}

/** A deleted project that syncs and imports leave out. */
export interface DeletedProject {
  id: number;
  project_id: number;
  name: string;
  /** Unix seconds. */
  removed_at: number;
  frames: number;
}

export type ProjectRemovalOutcome =
  | { state: 'removed'; name: string; targets: number; exposure_plans: number; frames: number; plan: string | null; plan_error: string | null }
  | { state: 'changed'; preview: ProjectRemovalPreview };

export interface ProjectSchedulerUpdate {
  name?: string;
  description?: string;
  state?: number;
  priority?: number;
  minimum_time?: number;
  minimum_altitude?: number;
  maximum_altitude?: number;
  use_custom_horizon?: boolean;
  horizon_offset?: number;
  meridian_window?: number;
  filter_switch_frequency?: number;
  dither_every?: number;
  enable_grader?: boolean;
  is_mosaic?: boolean;
  flats_handling?: number;
}

export interface TargetSchedulerUpdate {
  name?: string;
  project_id?: number;
  active?: boolean;
  ra_hours?: number;
  dec_degrees?: number;
  epoch_code?: number;
  rotation?: number;
  roi?: number;
}

export interface CreateExposurePlanRequest {
  exposure_template_id?: number;
  filter_name?: string;
  template_name?: string;
  gain?: number;
  offset?: number;
  bin?: number;
  readout_mode?: number;
  exposure: number;
  desired: number;
  enabled: boolean;
}

/** Sky coverage of one database: where its lights pointed and when. */
export interface SkyFilterTotal {
  filter: string;
  frames: number;
  accepted_frames: number;
  seconds: number;
  accepted_seconds: number;
}

export interface SkyFootprint {
  width_deg: number;
  height_deg: number;
  rotation_deg?: number | null;
  /** Sky per pixel, arcseconds, when the geometry is known. */
  pixel_scale_arcsec?: number | null;
  /** `solved` from a plate solution, `header` from a frame's FITS geometry. */
  source: 'solved' | 'header';
  /** ICRS vertices in boundary order, for a solved footprint. */
  vertices?: [number, number][];
}

/** The latest stack preview of a target, for drawing inside its field. */
export interface SkyPreview {
  /** Preview PNG URL, resolved against the backend by the API client. */
  url: string;
  /** The pixel grid the preview covers, in the stack's output orientation. */
  width: number;
  height: number;
  kind: 'color' | 'mono';
  filter?: string | null;
  /** Which stack this is, the same across rebuilds: `color:<composition>` or `mono:<filter>`. */
  key: string;
  /** The composition (`RGB`, `SHO`) or the filter. */
  label: string;
  /** The reference frame's plate solution, when the cache holds one. */
  wcs?: {
    crpix1: number;
    crpix2: number;
    crval1: number;
    crval2: number;
    cd11: number;
    cd12: number;
    cd21: number;
    cd22: number;
  } | null;
}

/** One panel of a project's mosaic: a target and its cell. */
export interface MosaicPanel {
  target_id: number;
  target_name: string;
  /** `r{row}c{column}`. */
  panel_id: string;
  /** One-based; row 1 at the top, column 1 at the left. */
  row: number;
  column: number;
  preview?: SkyPreview | null;
}

/** A project's targets as one mosaic. `director` when Director's
 *  activation record names the panels, `inferred` when the targets'
 *  coordinates form the grid. */
export interface ProjectMosaic {
  project_id: number;
  name: string;
  source: 'director' | 'inferred';
  rows: number;
  columns: number;
  /** Row by row from the top, left to right. */
  panels: MosaicPanel[];
}

export interface SkyTarget {
  id: number;
  name: string;
  project_id: number;
  project_name: string;
  ra_deg: number | null;
  dec_deg: number | null;
  rotation_deg: number | null;
  footprint: SkyFootprint | null;
  preview?: SkyPreview | null;
  frames: number;
  accepted_frames: number;
  seconds: number;
  accepted_seconds: number;
  nights: number;
  first_capture: number | null;
  last_capture: number | null;
  filters: SkyFilterTotal[];
}

export interface SkyNight {
  /** The civil date the night began on, `YYYY-MM-DD`. */
  night: string;
  target_id: number;
  filter: string;
  frames: number;
  accepted_frames: number;
  seconds: number;
  accepted_seconds: number;
}

export interface SkyTotals {
  frames: number;
  accepted_frames: number;
  seconds: number;
  accepted_seconds: number;
  nights: number;
  first_capture: number | null;
  last_capture: number | null;
  /** Seconds after 00:00 UTC at which this catalog's nights are split. */
  night_starts_utc_seconds: number;
}

export interface SkyCoverage {
  targets: SkyTarget[];
  nights: SkyNight[];
  filters: string[];
  totals: SkyTotals;
}

export interface OverallStats {
  total_projects: number;
  active_projects: number;
  total_targets: number;
  active_targets: number;
  total_images: number;
  accepted_images: number;
  rejected_images: number;
  pending_images: number;
  total_desired: number;
  files_found: number;
  files_missing: number;
  unique_filters: string[];
  date_range: DateRange;
  recent_activity: RecentActivity[];
  /**
   * Where this catalog's nights split, in seconds after 00:00 UTC: the
   * middle of the longest stretch of the day it never captured in. Images
   * groups by night, calibration and the AstroBin export all split here.
   * Absent from a sum over several catalogs.
   */
  night_boundary_seconds?: number;
}

export interface RecentActivity {
  date: number;
  images_added: number;
  images_graded: number;
}

export interface CacheRefreshProgress {
  is_refreshing: boolean;
  stage: string;
  progress_percentage: number;
  elapsed_seconds: number | null;
  directories_total: number;
  directories_processed: number;
  current_directory_name: string | null;
  files_scanned: number;
  projects_total: number;
  projects_processed: number;
  current_project_name: string | null;
  targets_total: number;
  targets_processed: number;
  files_found: number;
  files_missing: number;
  /** Started on its own (the cache expired, or the server started), not
   *  by Refresh. */
  automatic?: boolean;
}

// Sequence analysis types

/** Scoring overrides. The penalty scales say how hard event evidence hits
 * the quality score: a multiplier on the built-in penalty, where 0 ignores
 * the evidence, 1 (default) keeps calibrated behavior, and 2 deepens it.
 * The reject limits are absolute operator thresholds: recommend rejection
 * when measured HFR exceeds the limit or star count falls below it. */
export interface PenaltyScaleParams {
  penalty_satellite?: number;
  penalty_pointing?: number;
  penalty_temporal?: number;
  /** How hard an HFR above, or a star count below, the sequence's good
   *  frames marks a frame down: 0 not at all, 1 the calibrated band. */
  penalty_hfr_rise?: number;
  penalty_star_loss?: number;
  hfr_reject_above?: number;
  star_count_reject_below?: number;
}

export interface SequenceAnalysisRequest extends PenaltyScaleParams {
  target_id: number;
  filter_name?: string;
  session_gap_minutes?: number;
  weight_star_count?: number;
  weight_hfr?: number;
  weight_eccentricity?: number;
  weight_snr?: number;
  weight_background?: number;
  weight_spatial?: number;
  weight_pointing?: number;
}

export interface ProjectSequenceAnalysisRequest extends PenaltyScaleParams {
  project_id: number;
  filter_name?: string;
}

export interface DatabaseSequenceAnalysisRequest extends PenaltyScaleParams {
  all_projects: true;
  filter_name?: string;
}

export interface SequenceAnalysisResponse {
  sequences: ScoredSequence[];
  /** Scores across all stack candidates for each target/filter. Each score is
   * compared only with frames that have matching capture settings. */
  target_filter_rollups?: TargetFilterRollup[];
}

export interface TargetFilterScore {
  image_id: number;
  quality_score: number;
  normalized_metrics: ImageQualityResult['normalized_metrics'];
  details: string | null;
}

export interface TargetFilterRollup {
  target_id: number;
  target_name: string;
  filter_name: string;
  session_start?: number;
  session_end?: number;
  image_count: number;
  unavailable_image_count: number;
  images: TargetFilterScore[];
  summary: SequenceSummary;
}

export interface ScoredSequence {
  target_id: number;
  target_name: string;
  filter_name: string;
  session_start?: number;
  session_end?: number;
  image_count: number;
  reference_values: ReferenceValues;
  images: ImageQualityResult[];
  summary: SequenceSummary;
}

export interface QualityRegionOverlay {
  grid_cols: number;
  grid_rows: number;
  image_width: number;
  image_height: number;
  low_star_cells: boolean[];
  extinction_cells: boolean[];
  star_loss_cells: boolean[];
  background_rise_cells: boolean[];
  background_fall_cells: boolean[];
  glow_cells: boolean[];
}

export interface ImageQualityResult {
  image_id: number;
  quality_score: number;
  temporal_anomaly_score: number;
  category: string | null;
  flags?: string[];
  normalized_metrics: {
    star_count: number | null;
    hfr: number | null;
    eccentricity: number | null;
    snr: number | null;
    background: number | null;
    /** Spatial star coverage (1 = whole frame, 0 = half+ of grid cells dead).
     * Only populated when spatial metrics were computed from FITS files;
     * DB-metadata-only analysis leaves it null. Optional for older servers. */
    spatial_coverage?: number | null;
    /** Photometric transparency mapped to 0..1 (1 = nominal matched-star
     * flux, 0 = <=60% of the sequence reference). Populated after a spatial
     * scan; null otherwise. Optional for older servers. */
    transparency?: number | null;
    /** Pixel-derived pointing score. Missing until a quality scan runs. */
    pointing?: number | null;
  };
  pointing?: {
    pixel_solved: boolean;
    solve_failed: boolean;
    image_quality_evidence: boolean;
    expected_target: boolean;
    flags: string[];
    east_offset_arcsec?: number;
    north_offset_arcsec?: number;
    separation_arcsec?: number;
    field_fraction_offset?: number;
    reference_offset_arcsec?: number;
    /** Residual from the segment's own robust center, as a field fraction. */
    reference_field_fraction?: number;
    drift_rate_arcsec_per_hour?: number;
    /** Direction of celestial north from image up, degrees, positive toward image right. */
    field_rotation_deg?: number;
    field_mirrored?: boolean;
    /** This frame's rotation against its framing segment, modulo a half turn, in (-90, 90]. */
    rotation_skew_deg?: number;
    /** The rotation Target Scheduler planned for the target, as recorded. */
    planned_rotation_deg?: number;
    /** Solved minus planned rotation, modulo a half turn, so a meridian
     *  flip matches the plan. */
    planned_rotation_offset_deg?: number;
    /** The solved rotation sits more than 5° from the planned rotation, so
     *  the frame is flagged `rotation_skew`. */
    rotation_off_plan?: boolean;
    /** Set when the field turned away from the target's framing while the
     *  rotator reported no such turn: degrees from the reported angle. */
    rotator_skew_deg?: number;
    matched_stars?: number;
    rms_arcsec?: number;
    error?: string;
  };
  satellite?: {
    predicted_tracks: number;
    potentially_bright_count: number;
    high_risk_count: number;
    maximum_bright_trail_risk: number;
    pixel_alignment_attempted: boolean;
    pixel_aligned_count: number;
    pixel_aligned_high_risk_count: number;
    reject_recommended: boolean;
    association: 'predicted_not_pixel_detected' | 'predicted_pixel_checked' | 'predicted_with_pixel_alignment';
  };
  regrade_reason?: string;
  /** Measured grid cells that support a localized quality finding. Global
   * findings do not offer an overlay. */
  spatial_overlay?: QualityRegionOverlay;
  details: string | null;
}

export interface SpatialScanProgress {
  running: boolean;
  stage: string;
  target_id: number | null;
  filter_name: string | null;
  total: number;
  processed: number;
  skipped_cached: number;
  spatial_processed: number;
  astrometry_processed: number;
  solved: number;
  solve_failed: number;
  operational_errors: number;
  errors: number;
  current_file: string | null;
  started_at: number | null;
  finished_at: number | null;
  last_error: string | null;
  /** Started by background work (a database-wide analysis or new frames),
   *  not by a person. It steps aside while interactive work runs. */
  automatic?: boolean;
  /** What an automatic scan waits for while it steps aside, such as
   *  `a stack build`; null while it runs. */
  waiting_for?: string | null;
}

export interface SpatialScanStatus {
  /** POST: whether this request started a scan. GET: whether one is running. */
  started: boolean;
  progress: SpatialScanProgress;
  /** Images with cached spatial metrics in this database. */
  cached_count: number;
  /** Work left in a requested target/filter scope. GET only. */
  scope?: QualityScanScopeStatus;
}

export interface QualityScanScopeStatus {
  target_id: number;
  filter_name: string | null;
  total_frames: number;
  pending_frames: number;
  new_frames: number;
  outdated_frames: number;
  needs_analysis: boolean;
}

export interface SpatialScanStatusRequest {
  target_id?: number;
  filter_name?: string;
}

export interface SpatialScanRequest {
  target_id: number;
  filter_name?: string;
  force?: boolean;
  force_spatial?: boolean;
  force_astrometry?: boolean;
  force_satellites?: boolean;
  /** Write measured star count/HFR into imported images' metadata
   *  (missing keys only; default true). */
  fill_metadata?: boolean;
}

export interface QualityBackfillProgress {
  running: boolean;
  force: boolean;
  total_targets: number;
  processed_targets: number;
  current_target_id: number | null;
  started_at: number | null;
  finished_at: number | null;
}

export interface QualityBackfillStatus {
  started: boolean;
  progress: QualityBackfillProgress;
  /** Frames arriving by sync, upload or auto-import are analyzed too. */
  analyze_new_frames?: boolean;
  /** A person paused the analysis: it resumes at Resume or the next start. */
  paused?: boolean;
}

/** Calibrated and registered copies paired with their lights. */
export interface CalibratedCopies {
  /** Pair copies other software wrote with the light they came from. */
  pair: boolean;
  /** Measure quality on a light's calibrated copy instead of its raw file. */
  scan_calibrated: boolean;
  counts: {
    calibrated: number;
    registered: number;
    /** Lights whose own file is a calibrated copy (no raw frame yet). */
    calibrated_lights: number;
  };
}

export interface QualityBackfillRequest {
  force?: boolean;
  /** Write measured star count/HFR into imported images' metadata
   *  (missing keys only; default true). */
  fill_metadata?: boolean;
}

export interface SequenceSummary {
  excellent_count: number;
  good_count: number;
  fair_count: number;
  poor_count: number;
  bad_count: number;
  cloud_events_detected: number;
  focus_drift_detected: boolean;
  tracking_issues_detected: boolean;
  out_of_target_count: number;
  plate_solve_failed_count: number;
  /** Frames whose solved field rotation departs from their framing segment. */
  rotation_skew_count?: number;
  satellite_risk_count: number;
}

export interface ReferenceValues {
  best_star_count?: number;
  best_hfr?: number;
  best_eccentricity?: number;
  best_snr?: number;
  best_background?: number;
}

export interface ImageQualityResponse {
  image_id: number;
  quality?: ImageQualityResult;
  sequence_target_id?: number;
  sequence_filter_name?: string;
  sequence_image_count?: number;
  reference_values?: ReferenceValues;
}

/** A remote PSF Guard this instance can sync with. The key never leaves the server. */
export interface PeerSummary {
  id: string;
  name: string;
  base_url: string;
  catalog_id: string | null;
  token_configured: boolean;
}

export interface PeerCheck {
  reachable: boolean;
  product: string | null;
  product_version: string | null;
  protocol_version: number | null;
  catalogs: string[];
  capabilities: string[];
  error: string | null;
}

export type RemoteSyncDirection = 'pull' | 'push_planning' | 'push_grades';

export interface RemoteSyncRequest {
  peer_id: string;
  direction: RemoteSyncDirection;
  dry_run: boolean;
  reviewed_only?: boolean;
  with_image_data?: boolean;
  /** For a pull that writes: the projects its preview listed for deletion.
   *  The apply reads the peer again and deletes only these. */
  previewed_deletions?: string[];
}

export interface RemoteSyncResult {
  applied: boolean;
  peer_product: string;
  peer_catalog: string;
  summary: Record<string, number>;
  /** For a pull: GUIDs of local projects it deleted, or for a preview would
   *  delete, because the peer's catalog deleted them. Absent on older
   *  servers. */
  deleted_projects?: string[];
}
export type FlatHistoryState =
  | 'recorded' | 'pending' | 'removed' | 'absent' | 'conflict' | 'superseded';

export interface SchedulerFlatHistoryRecord {
  record_id: string;
  origin_id: string;
  source_name: string;
  source_row_id: number;
  fingerprint: string;
  target_guid: string | null;
  target_name: string | null;
  profile_id: string;
  light_session_date: number | null;
  light_session_id: number;
  flats_taken_date: number | null;
  flats_type: string | null;
  filter_name: string | null;
  gain: number | null;
  offset: number | null;
  bin: number | null;
  readout_mode: number | null;
  rotation: number | null;
  roi: number | null;
  state: FlatHistoryState;
  reason: string | null;
  invalidated_at: number | null;
  last_seen: number;
  acknowledged_at: number | null;
  detail: string | null;
}

export interface SchedulerFlatHistoryPage {
  records: SchedulerFlatHistoryRecord[];
  total: number;
}

/** One Target Scheduler table's rows without a GUID. */
export interface GuidGap { table: string; missing: number; total: number }
/** Rows Target Scheduler's migration left without a GUID, and whether this server can fill them. */
export interface GuidReport { tables: GuidGap[]; missing: number; writable: boolean }
/** What a fill did: rows given a GUID, and the copy taken first (none when nothing was missing). */
export interface GuidFillReport { tables: GuidGap[]; filled: number; backup_path: string | null }

/** A catalogued light, as a renamed-light pair shows it. */
export interface RenamedLight {
  id: number;
  guid: string;
  file_name: string;
  target: string | null;
  /** 0 pending, 1 accepted, 2 rejected. */
  grade: number;
  reject_reason: string | null;
}
/** A light whose file is gone, and the second light an import made of its renamed file. */
export interface RenamedPair { kept: RenamedLight; twin: RenamedLight; takes_grade: boolean }
/** A pair the merge leaves alone, and why. */
export interface HeldRenamedPair { kept: RenamedLight; twin: RenamedLight; reason: string }
/** Lights catalogued twice because their file was renamed after it was imported. */
export interface RenamedLightsReport {
  pairs: RenamedPair[];
  held: HeldRenamedPair[];
  writable: boolean;
  /** Image folders missing now; nothing is checked until they are back. */
  missing_folders: string[];
}
/** What a merge did, and the copy taken first (none when there was nothing to merge). */
export interface RenamedLightsMerged { merged: RenamedPair[]; held: HeldRenamedPair[]; backup_path: string | null }

/** One volume holding database caches, as the last disk check found it. */
export interface CacheVolumeReport {
  path: string;
  databases: string[];
  total_bytes: number;
  used_bytes: number;
  /** As `df` reports it. */
  used_percent: number;
  max_percent: number;
  /** Image previews, annotated previews and star lists: culled first. */
  preview_bytes: number;
  /** Stacks, color previews, WBPP stacks and their checkpoints. */
  stack_bytes: number;
  calibration_bytes: number;
  other_bytes: number;
  culled_files: number;
  freed_bytes: number;
  /** Still over the limit with nothing left that may be culled. */
  over_limit: boolean;
  /** Which of the cache, stacks and calibration masters live here. */
  kinds: StorageFolderKind[];
  checked_unix: number;
}

export type StorageFolderKind = 'cache' | 'stacks' | 'calibration';

export interface StorageFolder {
  kind: StorageFolderKind;
  /** In use now. */
  path: string;
  /** What the next start uses; differs from `path` until a restart. */
  next_path: string;
  /** `server_config` means the config file or command line fixes it. */
  source: 'server_config' | 'settings' | 'default';
  /** The folder chosen in Settings, if one is. */
  chosen: string | null;
}

export interface StorageSettings {
  /** The cache's limit; 100 turns culling off. */
  max_volume_percent: number;
  /** Own limits; null shares the cache's. */
  stack_max_volume_percent: number | null;
  calibration_max_volume_percent: number | null;
  default_max_volume_percent: number;
  min_max_volume_percent: number;
  volumes: CacheVolumeReport[];
  /** Cache, stacks and calibration masters, in that order. */
  folders: StorageFolder[];
  /** What this start's folder move did. */
  folder_notes: string[];
  can_choose_folders: boolean;
}

/** Absent keeps a limit; null shares the cache's. */
export interface StorageLimitsUpdate {
  max_volume_percent?: number;
  stack_max_volume_percent?: number | null;
  calibration_max_volume_percent?: number | null;
}

/** Empty or absent goes back to the default. */
export interface StorageFoldersUpdate {
  cache_dir?: string;
  stack_dir?: string;
  calibration_dir?: string;
}

/** One rejected frame a removal takes, and its files. */
export interface RejectRemovalFrame {
  image_id: number;
  guid: string;
  target_id: number | null;
  target_name: string;
  file_name: string | null;
  rejected_at: number;
  reject_reason: string | null;
  files: Array<{ kind: 'light' | 'sidecar' | 'copy'; path: string; bytes: number }>;
}
/** A reject a removal leaves, and why. */
export interface RejectRemovalSkip {
  image_id: number;
  guid: string | null;
  target_name: string;
  file_name: string | null;
  reason: string;
}
/** What a removal would take; Apply sends its digest back. */
export interface RejectRemovalPlan {
  min_age_days: number;
  frames: RejectRemovalFrame[];
  skipped: RejectRemovalSkip[];
  /** Rejects still inside the grace period. */
  waiting: number;
  next_eligible_at: number | null;
  without_files: number;
  bytes: number;
  digest: string;
}
export interface RejectRemovalReport {
  batch_id: string;
  removed: Array<{ image_id: number; guid: string }>;
  failed: RejectRemovalSkip[];
  files_moved: number;
  bytes: number;
  trash_until: number;
  /** Stopped before every frame went: `removed` lists those that did. */
  stopped?: boolean;
}
export interface RemovedRejectBatch {
  batch_id: string;
  removed_at: number;
  frames: number;
  bytes: number;
  trash_until: number;
  /** Frames whose files are already gone from the trash. */
  files_deleted: number;
  /** Frames whose saved rows were purged; only markers remain. */
  purged: number;
}
export interface RemovedRejects {
  batches: RemovedRejectBatch[];
}
export interface RejectRestoreReport {
  restored: Array<{ guid: string; image_id: number }>;
  failed: RejectRemovalSkip[];
  renamed: string[];
  /** Stopped before every frame came back. */
  stopped?: boolean;
}
export interface RejectTrashReport {
  frames: number;
  files_deleted: number;
  bytes: number;
  problems: string[];
  /** Stopped before every removal past its retention was emptied. */
  stopped?: boolean;
}
/** What the last Remove, Restore or Empty trash did, and its run. */
export interface RejectRemovalOutcome {
  run: number;
  finished_at: number;
  action: 'remove' | 'restore' | 'empty_trash';
  removed?: RejectRemovalReport;
  restored?: RejectRestoreReport;
  emptied?: RejectTrashReport;
}
/** A catalog's reject removal job: Preview, Remove, Restore and Empty trash
 *  read the image folders and move files, so each runs as a job. */
export interface RejectRemovalStatus {
  state: 'idle' | 'running' | 'done' | 'stopped' | 'failed';
  /** What the run is, such as `Removing rejects`. */
  title: string;
  /** The stage and count, such as `Moving to the trash 37 of 203`. */
  label: string;
  /** 0–100 for the stage, or null before its size is known. */
  percent: number | null;
  action: 'preview' | 'remove' | 'restore' | 'empty_trash' | null;
  stage: 'indexing' | 'checking' | 'moving' | 'restoring' | 'deleting' | null;
  done: number;
  total: number;
  /** Counts up with each run, so a page knows which result is its own. */
  run: number;
  started_at: number | null;
  finished_at: number | null;
  /** The latest preview; a removal that applied leaves none. */
  plan: RejectRemovalPlan | null;
  /** A removal found the rejects changed, removed nothing, and left the
   *  new preview in `plan`. */
  changed: boolean;
  outcome: RejectRemovalOutcome | null;
  error: string | null;
  priority: string | null;
}
export interface RejectPurgeReport {
  frames: number;
  bytes: number;
}

/** Flat groups: nights whose flats measure alike share a group. Numbers
 *  named `_pct` are percent. */
export interface FlatGroupsView {
  /** False when the catalog has no flat-group tables (read-only or old). */
  available: boolean;
  rigs: FlatGroupsRig[];
}

export interface FlatGroupsRig {
  rig_uuid: string;
  name: string;
  /** False: this rig's setting groups its flats by time only. */
  measured: boolean;
  dust_limit_pct: number;
  pattern_limit_pct: number;
  /** The flat-group settings the rig sets for itself; the rest it inherits. */
  own_limits?: string[];
  angle_term: FlatPairDifference | null;
  angle_pairs: number;
  /**
   * The rig's clock over time, oldest first, one run per change: its
   * `night_zone` setting, else what its frames' clocks record. Its times
   * read on it; empty, on the viewer's clock.
   */
  clock?: FlatClockRun[];
  /** Newest first. */
  events: FlatChangeEvent[];
  families: FlatFamily[];
  unmeasured: FlatUnmeasuredSession[];
  /** A person's splits and joins that find nothing to apply to now. */
  orphaned?: FlatGroupOverride[];
}

/** A person's split or join of one family's groups. */
export interface FlatGroupOverride {
  choice: 'split' | 'join' | null;
  /** The edge in words when it was made. */
  label: string;
  chosen_at: number;
  /** The recorded edge: what a reset names. */
  anchor_frame: string;
  /** In force: whether the measurements alone group the same. */
  agrees: boolean | null;
  /** Why it no longer applies; it stays until reset. */
  orphaned: string | null;
  /** A camera-side change it stands against; both stay in force. */
  conflict: {
    key: string;
    start: number;
    end: number;
    what: string;
    filters: string[];
  } | null;
}

export interface FlatClockRun {
  /** Unix seconds of the first frame on this offset. */
  from: number;
  offset_seconds: number;
}

export interface FlatPairDifference {
  dust_pct: number;
  broad_pct: number;
}

export interface FlatUnmeasuredSession {
  night: string;
  filter: string | null;
  rotation: number | null;
  frames: number;
  reason: string;
  /** The Calibration page's sessions that hold its flats. */
  sessions?: string[];
}

/** Every spot of a comparison, counted before its list is cut to the
 *  deepest 12. */
export interface FlatSpotTotals {
  /** Spots that count: not moved within focus travel. */
  counted: number;
  within_tolerance: number;
  by_kind: Record<string, number>;
  by_shape: Record<string, number>;
  /** How many `spots` lists. */
  listed: number;
}

/** A change seen across one rig's flats at once. */
export interface FlatChangeEvent {
  key: string;
  /** Unix seconds: the window the change happened in. */
  start: number;
  end: number;
  what: string;
  dust_pct: number;
  broad_pct: number;
  camera_side: boolean;
  filters: string[];
  rotations: (number | null)[];
  vignetting_pct?: number;
  vignetting?: FlatVignettingChange[];
  /** The deepest 12 spots; `spot_totals` counts every one. */
  spots: FlatSpot[];
  spot_totals?: FlatSpotTotals;
  /** The pair whose ratio picture shows it. */
  before_response: string;
  after_response: string;
  /** The window is shorter than one flat run: it landed between one twilight's flats, or two changes met there. */
  within_flat_run?: boolean;
}

/** A shadow that came, went or moved; position and radius in sensor pixels. */
export interface FlatSpot {
  kind: 'new' | 'gone' | 'moved';
  darker: boolean;
  x: number;
  y: number;
  radius: number;
  peak_pct: number;
  shape?: DustShape;
  /** A donut's hole, as a fraction of `radius`. */
  hole?: number | null;
  /** Left, top, right, bottom. */
  bounds?: [number, number, number, number];
  /** Dust that moved no further than focus travel: drawn, not counted. */
  within_tolerance?: boolean;
  offset_px?: number | null;
}

/** A change of vignetting between two flat sessions. */
export interface FlatVignettingChange {
  edge: VignettingEdge;
  darker: boolean;
  x: number;
  y: number;
  area: number;
  depth_pct: number;
}

/** One filter's response family on one rig: the angles whose flats
 *  measure alike. */
export interface FlatFamily {
  key: string;
  filter: string | null;
  /** The middle angle of its largest rotation cluster. */
  rotation: number | null;
  /** Its measured angle range, one span per rotation cluster that joined. */
  angles?: { low: number; high: number }[];
  /** That range in words: "100.1–104.4°", "72.6° & 82.2°", "no angle". */
  angle_label?: string;
  /** Newest first. */
  groups: FlatGroup[];
  /** `splits[i]` lies between `groups[i + 1]` (older) and `groups[i]` (newer). */
  splits: FlatSplit[];
  /** Measured splits a person joined, newest first; each lies inside a group now. */
  joins?: FlatSplit[];
}

export interface FlatGroup {
  /** Stable across a regroup where it can be: the first, by id, of the
   *  Calibration page's sessions holding its oldest member. Any member's
   *  session finds the group again. */
  key: string;
  first_night: string;
  last_night: string;
  frames: number;
  /** Newest first. */
  sessions: FlatSession[];
  /** Why they share a group: each session against the next in time, and
   *  the pairs that differ most in dust and in light pattern, oldest first. */
  pairs?: FlatMemberPair[];
  /** The most any two of its sessions measured apart, over every pair
   *  measured, with how many were. */
  widest?: { dust_pct: number; broad_pct: number; compared: number } | null;
}

/** Two sessions of one group, measured against each other. */
export interface FlatMemberPair {
  earlier_response: string;
  later_response: string;
  earlier_night: string;
  later_night: string;
  dust_pct: number;
  broad_pct: number;
}

/** Lights by how their flats were chosen. */
export interface FlatGroupLightCounts {
  total: number;
  /** The nearest flats, or its own side of each change. */
  own: number;
  other_angle: number;
  corrected: number;
  undetermined: number;
  no_same_side: number;
  /** A person chose this group's flats for it. */
  chosen?: number;
}

/** What one flat group feeds: its masters and the lights that take it. */
export interface FlatGroupUse {
  key: string;
  masters: { id: string; label: string; created_at: number; sources: number; in_group: number }[];
  lights: FlatGroupLightCounts;
  projects: {
    project_id: number;
    name: string | null;
    lights: FlatGroupLightCounts;
    /** The lights by id, sent only for the group asked for. */
    image_ids?: number[];
    /** Of them, those flagged for their flats. */
    flagged_ids?: number[];
  }[];
}

export interface FlatGroupUseView {
  /** False until the first answer is worked out: nothing else is known. */
  computed: boolean;
  /** Groups something uses; one not listed feeds nothing. */
  groups: FlatGroupUse[];
  lights: number;
  /** Lights the calibration check has not read yet. */
  pending: number;
  unread: number;
  /** A newer answer is being worked out; this is the last one kept. */
  refreshing: boolean;
  /** Why the last attempt at the current answer failed; tried again later. */
  error?: string | null;
  /** Asked for one group: whether its key names a group now. */
  group_found?: boolean | null;
  /** The key of the group asked for, when found by one of its sessions. */
  group_key?: string | null;
}

/** Where a flat session would sit among the flat groups. */
export interface FlatSessionPlace {
  rig_uuid: string | null;
  /** A family's key begins with it and `:`. */
  family_key: string | null;
  night: string | null;
  rotation: number | null;
}

export interface FlatSession {
  response_key: string;
  night: string;
  first: number;
  last: number;
  frames: number;
  combined: number;
  noise_pct: number | null;
  /** The response grid is the sensor binned by `bin` into width × height. */
  bin: number | null;
  width: number | null;
  height: number | null;
  /** Against the session before it in its family. */
  previous: FlatPairDifference | null;
  /** The Calibration page's sessions that hold its flats. */
  sessions?: string[];
}

export interface FlatSplit {
  before_response: string;
  after_response: string;
  before_night: string;
  after_night: string;
  start: number;
  end: number;
  dust_pct: number | null;
  broad_pct: number | null;
  vignetting_pct?: number | null;
  vignetting?: FlatVignettingChange[];
  reason: string;
  event_key: string | null;
  /** The deepest 12 spots; `spot_totals` counts every one. */
  spots: FlatSpot[];
  spot_totals?: FlatSpotTotals;
  /** `split`: a person split where the nights measure alike; `joined`: a
   *  measured split a person joined. The numbers are the measurements. */
  by_hand?: 'split' | 'joined' | null;
  /** The person's split or join at this edge. */
  choice?: FlatGroupOverride | null;
}

/** The changes a set of lights were shot during, for the Images bulk action. */
export interface FlatLightChoiceOptions {
  changes: {
    key: string;
    /** The change's rig: its window reads on the rig's clock. */
    rig_uuid: string;
    start: number;
    end: number;
    what: string;
    camera_side: boolean;
    filters: string[];
    /** Of the lights, those shot inside its window. */
    lights: number;
    chosen_before: number;
    chosen_after: number;
    /** Those lights, by id: what a side for the change is sent for. */
    image_ids: number[];
  }[];
  /** Lights with any choice in force. */
  with_choices: number;
  without_guid: number;
}

/** What the calibration quality job is doing. */
export interface CalibrationQualityProgress {
  running: boolean;
  stage: string | null;
  to_measure: number;
  done: number;
  workers: number;
  started_at: number | null;
  finished_at: number | null;
  last_error?: string | null;
}

/** What one pass of the frame-sites job did. */
export interface FrameSitesOutcome {
  pending: number;
  done: number;
  located: number;
  clocked: number;
  unplaced: number;
  unreadable: number;
  /** No file could be read, as when the volume is not mounted. */
  volume_away: boolean;
  error: string | null;
  placed: string[];
}

/** The frame-sites job of one catalog: reads the site of each calibration
 *  frame imported before the catalog kept one. */
export interface FrameSitesStatus {
  /** `paused` while a person paused the job. */
  state: 'idle' | 'queued' | 'running' | 'paused';
  /** The progress in words, such as `Reading frame sites · 812 of 1,867 frames`. */
  label: string;
  /** A pass is reading frames: what the job line shows. */
  active: boolean;
  done: number;
  total: number;
  /** While a pass runs, frames came since it began: another pass reads
   *  them after this one. */
  next_pass?: boolean;
  waiting_for: string | null;
  retry_at: number | null;
  started_at: number | null;
  finished_at: number | null;
  last: FrameSitesOutcome | null;
  last_error: string | null;
}

/** The flat measuring job of one catalog: responses, comparisons and light
 *  evidence, one rig at a time. */
export interface FlatMeasureStatus {
  /** `queued` while requests settle, grading runs or a retry waits;
   *  `paused` while a person paused the job. */
  state: 'idle' | 'queued' | 'running' | 'paused';
  /** The step in words, such as `Measuring flats · 12 of 40 sessions`. */
  label: string;
  /** A pass found work and is doing it: what the job line shows. */
  active: boolean;
  rig_uuid: string | null;
  rig_name: string | null;
  /** `measuring flat sessions`, `comparing flat sessions`, or `reading
   *  lights shot during a change`. */
  stage: string | null;
  done: number;
  total: number;
  /** Rigs whose work waits or runs; `*` for every rig. */
  rigs: string[];
  /** Rigs the running pass measures after this one. */
  rigs_after?: number;
  /** While a pass runs, the rigs asked for since it began, or due a
   *  retry, which the next pass takes; `*` for every rig. */
  next_pass?: string[];
  /** When the next retry of unfinished work is due, Unix seconds. */
  retry_at: number | null;
  /** `viewed` while a page shows the rig. */
  priority: 'background' | 'viewed' | null;
  /** What a person started that the pass waits for, while it does. */
  waiting_for: string | null;
  workers: number;
  started_at: number | null;
  finished_at: number | null;
  last: {
    sessions: number;
    measured: number;
    unreadable: number;
    compared: number;
    evidence: number;
    frames_read: number;
    lights_missing: number;
    retry: string[];
    stopped: boolean;
  } | null;
  last_error: string | null;
}

export interface CalibrationQualityStatus {
  progress: CalibrationQualityProgress;
}
